import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useInfiniteQuery, useMutation, useQueryClient,
  type InfiniteData, type QueryClient, type QueryKey, type QueryState,
} from '@tanstack/react-query';
import { API_BASE_URL } from '@api/config';
import type { ViewSnapshot } from '@api/apps';
import client, { refreshAuthToken } from '@api/client';
import { tokenLasting } from '@api/tokenLifetime';
import useConversations from '@contexts/useConversations';
import useAuth from '@contexts/useAuth';
import { useSseStream, type SseEvent } from './useSseStream';
import { approveToolExecution, rejectToolExecution } from '@api/toolApprovals';
import { INTERNAL_TOOLS, parseToolApprovalRequest } from '@utils/toolApprovalRequest';
import { agentDisplayName, ORCHESTRATOR_AGENT } from '@constants/agentNames';
import { trackFunnelEvent } from '@utils/datadog';
import {
  getStoredLlmModel,
  setStoredLlmModel,
  LLM_MODEL_AUTO,
  getDismissedModelSuggestions,
  addDismissedModelSuggestion,
} from '@utils/llmModel';
import { SETUP_EXIT_MS } from '@components/Chat/SetupStatus.constants';

// ── Internal / meta tools suppressed from the UI ─────────────────────────────

/**
 * Tools that are internal plumbing and must never appear as tool chips.
 * Add any new meta/orchestration tools here to keep the UI clean.
 */
function getInsufficientCreditsMessage(canManageBilling: boolean, hasActiveSubscription: boolean | null | undefined): string {
  if (!canManageBilling) return 'You have no credits remaining. Please contact your organization administrator.';
  if (hasActiveSubscription === false) return 'You have no credits remaining. Upgrade your plan to continue using the agent.';
  return 'You have no credits remaining. Purchase more credits to continue using the agent.';
}

/** Clears any pending_approval tool chips when the user sends a new message. */
function clearPendingApprovals(m: Message): Message {
  if (!m.toolExecutions?.some((t) => t.status === 'pending_approval')) return m;
  return {
    ...m,
    toolExecutions: m.toolExecutions.map((t) =>
      t.status === 'pending_approval' ? { ...t, status: 'complete' as const, success: false } : t,
    ),
  };
}

const LOAD_SKILL_TOOL = 'load_skill';

/** The absolute offset the backend sent for the tool, or a local fallback. */
const toolInsertPosition = (data: Record<string, unknown>, fallback: number): number =>
  typeof data.insertPosition === 'number' ? data.insertPosition : fallback;

// ── Public types ─────────────────────────────────────────────────────────────

export interface MessageAttachment {
  attachmentId: string;
  filename: string;
  contentType: string;
  fileSize: number;
  contextStrategy: string;
  url: string | null;
  /**
   * A reduced copy to render from, when the server kept one because the upload
   * was too large to show a model. Absent for everything else, which is its
   * own preview.
   */
  previewUrl?: string | null;
  previewFileSize?: number | null;
  /** The reduced copy's filename; its extension is how a viewer picks a loader. */
  previewName?: string | null;
}

export interface AgentBoundary {
  agent: string;
  displayName: string;
  startPos: number;
}


export interface InlineArtifact {
  artifactId: string;
  name: string;
  contentType: string;
  downloadUrl: string;
  fileSize?: number;
  insertPosition?: number;
  moderationBlocked?: boolean;
}

export interface ModelSuggestion {
  message: string;
  taskType: string;
  suggestedModel: string;
  suggestedModelLabel: string;
}

// Sent as the user turn when the user resolves a model-swap suggestion (AIB-7877).
// The original request is already in history, so a short reply is enough for the
// (newly selected or unchanged) model to proceed.
const MODEL_SWAP_PROCEED_MESSAGE = 'Yes, switch the model and continue.';
const MODEL_KEEP_PROCEED_MESSAGE = 'No, continue with the current model.';

export type AskUserAnswerType = 'option' | 'decide_for_me' | 'free_text' | 'region';

const ASK_USER_ANSWER_TYPES = ['option', 'decide_for_me', 'free_text', 'region'] as const;

export interface AskUserQuestion {
  id: string;
  question: string;
  answers: string[];
  allowDecideForMe?: boolean;
  allowFreeText?: boolean;
  /** Artifact id of a highlighted drawing to show above the question (AIB-7965). */
  imageArtifactId?: string;
  /** norm_bbox [x0,y0,x1,y1] (0-1 fractions) of the region to ring on the image. */
  region?: [number, number, number, number];
  regionLabel?: string;
  /** Annotate mode (AIB-7965): the user draws a box on the image; the drawn
   *  region is the answer and a crop of it is sent to the agent. */
  expectsRegion?: boolean;
}

export interface AskUserAnswer {
  question: string;
  options: string[];
  answer: string;
  answerType: AskUserAnswerType;
  /** norm_bbox [x0,y0,x1,y1] the user drew, for an 'region' answer (AIB-7965). */
  region?: [number, number, number, number];
}

export interface PendingAskUser {
  askId: string;
  questions: AskUserQuestion[];
  // In-progress answers/crops to re-seed the card with after a failed send
  // remounts it, so the user's drawn region and answers aren't lost.
  restore?: { answers: AskUserAnswer[]; crops: File[] };
}

/**
 * Body sent with a clarification turn. For a real submission every item is a
 * full {@link AskUserAnswer}; for a skip we send only the question + options
 * legend (no `answerType`) so the worker can still print the lettered legend
 * while reload knows not to rebuild an answers summary.
 */
type ClarificationItem = Pick<AskUserAnswer, 'question' | 'options'> & Partial<AskUserAnswer>;

interface ClarificationSubmission {
  questionAnswers?: ClarificationItem[];
  questionsSkipped?: boolean;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date | string;
  /** When the turn finished; unset while it is still running. */
  completedAt?: Date | string;
  /** Id the row streamed under before `done` swapped in the DB id; keeps the DOM key stable. */
  clientId?: string;
  toolExecuting?: string;
  toolExecutions?: ToolExecution[];
  attachments?: MessageAttachment[];
  /** Model-swap suggestion this assistant turn surfaced (AIB-7877) — restores the swap card on reload. */
  modelSuggestion?: ModelSuggestion;
  /** Clarification questions this assistant turn asked (AIB-6745) — used to restore the dock card on reload. */
  askUserQuestions?: AskUserQuestion[];
  /** Answers the user submitted to a clarification batch — rendered as a summary on the user message. */
  askUserAnswers?: AskUserAnswer[];
  guardrailRetracted?: boolean;
  guardrailPolicy?: string;
  /** Set when the turn ended unfinished or without a reply; renders a retry notice instead of the feedback bar. */
  interrupted?: boolean;
  /** `termination_reason` the turn was recorded with (e.g. `cancelled`, `no_reply`); picks the notice copy. */
  interruptReason?: string;
  isStreaming?: boolean;
  insufficientCredits?: { canManageBilling: boolean; hasActiveSubscription: boolean | null | undefined };
  feedbackRating?: 'up' | 'down';
  inlineArtifacts?: InlineArtifact[];
  agentBoundaries?: AgentBoundary[];
}

export interface ArtifactInfo {
  artifactId?: string;
  name: string;
  fileSize: number;
  downloadUrl?: string;
  localPath?: string;
  imageUrl?: string;
  description?: string;
  artifactType?: string;
  /** Only inline artifacts carry one; a runner's is inferred from the name. */
  contentType?: string;
  /** URL of an internal artifact generated for 3D preview of non-mesh CAD files. */
  previewUrl?: string;
  /** Filename of that preview — its extension picks the loader (STL or GLB). */
  previewName?: string;
  /** Absent for runners that predate it; the preview can be far larger than the CAD file. */
  previewFileSize?: number;
}

export interface ToolExecution {
  tool: string;
  toolUseId?: string;
  status: 'executing' | 'complete' | 'pending_approval';
  success?: boolean;
  insertPosition?: number;
  artifactInfo?: ArtifactInfo;
  imageUrl?: string;
  /** Serialized args JSON for tools awaiting user approval. */
  approvalArgs?: string;
  /** Short AI-generated phrase summarizing the tool action (4-5 words). */
  summary?: string;
  /** Name of the sub-agent that invoked this tool (e.g. "search", "cadquery"). */
  agentName?: string;
}

/**
 * A preparation step the agent takes before it can do any real work (loading a
 * skill, booting the execution environment).  Steps are live status only: a
 * skill step is handed over to a durable `ToolExecution` when the load
 * finishes, and the environment step is dropped when the turn ends.
 */
export interface SetupStep {
  /** toolUseId for skills; the literal 'environment' for the environment step. */
  id: string;
  kind: 'skill' | 'environment';
  status: 'active' | 'done';
  /** Raw skill id (e.g. "cadquery") for skill steps. */
  skill?: string;
  /** Offset the load started at, carried over to the durable transcript entry. */
  insertPosition?: number;
  /** When allocation began — drives the elapsed timer on the environment step. */
  startedAt?: number;
}

// ── Internal types ───────────────────────────────────────────────────────────

interface BackendAttachment {
  attachmentId: string;
  filename: string;
  contentType: string;
  fileSize: number;
  contextStrategy: string;
  url: string | null;
  previewUrl?: string | null;
  previewFileSize?: number | null;
  previewName?: string | null;
  createdAt?: string;
}

interface BackendMessage {
  messageId: string;
  role: string;
  content: string;
  toolName?: string;
  correlationId?: string;
  createdAt?: string;
  attachments?: BackendAttachment[];
  retracted?: boolean;
  metadata?: string;
  success?: boolean;
  feedbackRating?: 'up' | 'down';
}

interface PaginatedMessagesResponse {
  messages: BackendMessage[];
  hasMore: boolean;
}

interface MessagePage {
  messages: Message[];
  hasMore: boolean;
  oldestTimestamp: string | null;
}

// ── Constants & helpers ──────────────────────────────────────────────────────

const PAGE_SIZE = 50;

/**
 * Fallback attempt ceiling, used only while the server has not told us how long
 * to keep trying. A takeover is bounded by a deadline instead (see
 * `ServerShutdownError`), because the thing worth waiting for is a lock being
 * released, and a count cannot know how long that takes.
 */
const MAX_RECONNECT_RETRIES = 20;

/**
 * How long a send waits for an in-flight page-0 fetch before cancelling it
 * anyway. Bounded because a fetch the network paused never settles on its own,
 * and a send that hangs is worse than a transcript that reloads a turn later.
 */
const HISTORY_SETTLE_BUDGET_MS = 1_500;
// Past the control plane's own wait for a stopped turn to be saved.
const STOPPED_TURN_LATE_RESYNC_MS = 8_000;

/**
 * Thrown on a `server_shutdown` event.
 *
 * `retryForMs` is present when the server reported that another instance still
 * holds this turn's monitoring lock, and says how long that lock can still be
 * held. It comes from `ShutdownBudget.takeoverRetryBudget()` rather than being
 * chosen here: the client giving up while the lock is legitimately held is what
 * loses a turn the server had already preserved for handover (AIB-8064).
 */
class ServerShutdownError extends Error {
  constructor(readonly retryForMs?: number) {
    super('SSE_SERVER_SHUTDOWN');
    this.name = 'ServerShutdownError';
  }
}

/** Retry delay for transient reconnect failures. */
const reconnectRetryDelayMs = (errMessage: string, retryCount: number): number =>
  errMessage === 'SSE_SERVER_SHUTDOWN' ? 3_000 : Math.min(500 * 2 ** retryCount, 8_000);

const isRetryableReconnectError = (err: unknown): err is Error =>
  err instanceof Error &&
  (err.message === 'SSE_SERVER_SHUTDOWN' || err.message === 'SSE_STALE_CONNECTION');

/** Absolute time to keep retrying until, or `null` if the server named no budget. */
const takeoverDeadline = (err: unknown): number | null =>
  err instanceof ServerShutdownError && err.retryForMs !== undefined
    ? Date.now() + err.retryForMs
    : null;

interface ReconnectBudget {
  /** Deadline to carry into the next attempt, or `null` while the count governs. */
  deadline: number | null;
  exhausted: boolean;
}

/**
 * Decides whether to keep asking for a takeover, and on what basis.
 *
 * A deadline advertised by the server governs once we have one, because it knows
 * how long the lock being waited on can be held and an attempt count cannot. It
 * is learned from whichever response first reports `takeover_pending` and then
 * carried forward, so later attempts do not keep pushing it back. With no
 * deadline — an older backend, a plain `deployment` shutdown, a stale connection
 * — the attempt ceiling applies exactly as it did before.
 */
const reconnectBudget = (
  err: unknown,
  retryCount: number,
  carried: number | null,
): ReconnectBudget => {
  const deadline = carried ?? takeoverDeadline(err);
  return {
    deadline,
    exhausted: deadline !== null
      ? Date.now() >= deadline
      : retryCount >= MAX_RECONNECT_RETRIES,
  };
};

const isAbortedOrCancelled = (err: unknown, signal: AbortSignal): boolean =>
  signal.aborted || (err instanceof DOMException && err.name === 'AbortError');

// fetch() and reader.read() reject with a bare TypeError on network failure; the
// message is browser-specific ("Failed to fetch", "Load failed", ...), so match the class.
export const isNetworkError = (err: unknown): boolean => err instanceof TypeError;

const conversationQueryKey = (conversationId: string | null | undefined) =>
  conversationId ? (['conversation', conversationId] as const) : (['conversation', 'none'] as const);

const getAuthToken = (): string | null => {
  const raw = localStorage.getItem('aibuild_user') || localStorage.getItem('user');
  if (!raw) return null;
  try {
    return JSON.parse(raw).token ?? null;
  } catch {
    return null;
  }
};

const positiveSize = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;

const parseArtifactInfo = (content: string): ArtifactInfo | undefined => {
  try {
    const p = JSON.parse(content);
    const description: string | undefined = p.description || undefined;
    const artifactType: string | undefined = p.artifact_type || undefined;
    const preview = {
      previewUrl: p.preview_url || undefined,
      previewName: p.preview_name || undefined,
      previewFileSize: positiveSize(p.preview_file_size),
    };
    if (p.local_path) {
      return {
        name: p.name || 'Artifact',
        fileSize: p.file_size || 0,
        localPath: p.local_path,
        imageUrl: p.image_url || undefined,
        description,
        artifactType,
        ...preview,
      };
    }
    if (p.artifact_id && p.download_url) {
      return { artifactId: p.artifact_id, name: p.name || 'Artifact', fileSize: p.file_size || 0, downloadUrl: p.download_url, description, artifactType, ...preview };
    }
  } catch { /* not JSON */ }
  return undefined;
};

/** Reconstruct a model-swap suggestion (AIB-7877) from an assistant turn's metadata. */
export const parseModelSuggestion = (metadata?: string): ModelSuggestion | undefined => {
  if (!metadata) return undefined;
  try {
    const raw = JSON.parse(metadata)?.model_suggestion;
    return normalizeModelSuggestion(raw);
  } catch { return undefined; }
};

/** Normalise a model-suggestion payload (from SSE or metadata) into a typed object. */
export const normalizeModelSuggestion = (raw: unknown): ModelSuggestion | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const rec = raw as Record<string, unknown>;
  const message = typeof rec.message === 'string' ? rec.message : '';
  const suggestedModel = typeof rec.suggestedModel === 'string' ? rec.suggestedModel : '';
  if (!message || !suggestedModel) return undefined;
  return {
    message,
    suggestedModel,
    taskType: typeof rec.taskType === 'string' ? rec.taskType : '',
    suggestedModelLabel:
      typeof rec.suggestedModelLabel === 'string' && rec.suggestedModelLabel
        ? rec.suggestedModelLabel
        : suggestedModel,
  };
};

/** Validate a norm_bbox: 4 finite numbers in [0,1] with x0<x1 and y0<y1 (AIB-7965). */
const parseRegion = (raw: unknown): [number, number, number, number] | undefined => {
  if (!Array.isArray(raw) || raw.length !== 4) return undefined;
  const box = raw.map((v) => (typeof v === 'number' ? v : Number.NaN));
  if (box.some((v) => !Number.isFinite(v) || v < 0 || v > 1)) return undefined;
  if (box[0] >= box[2] || box[1] >= box[3]) return undefined;
  return [box[0], box[1], box[2], box[3]];
};

/** Optional image/region fields, merged onto a question. Reads snake_case
 * (persisted model tool_use args) or camelCase (live SSE) keys (AIB-7965). */
const parseQuestionImage = (rec: Record<string, unknown>): Partial<AskUserQuestion> => {
  const rawId = rec.image_artifact_id ?? rec.imageArtifactId;
  if (typeof rawId !== 'string' || !rawId.trim()) return {};
  const out: Partial<AskUserQuestion> = { imageArtifactId: rawId.trim() };
  const label = rec.region_label ?? rec.regionLabel;
  if (typeof label === 'string' && label.trim()) out.regionLabel = label.trim();
  if (rec.expects_region === true || rec.expectsRegion === true) {
    // Annotate mode: the user draws the box, so the agent's own region ring
    // is meaningless here — keep only the label + draw surface.
    out.expectsRegion = true;
    return out;
  }
  const region = parseRegion(rec.region);
  if (region) out.region = region;
  return out;
};

const asToolArgs = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

/** Read an `ask_user` call's arguments off a persisted content block. Canonical blocks are
 *  `tool_request`/`tool_name`/`tool_input`; rows written before the provider-agnostic LLM
 *  refactor carry the Anthropic `tool_use`/`name`/`input` shape, and Bedrock streaming can
 *  leave the args as a JSON string (AIB-8140). */
const askUserToolInput = (block: unknown): Record<string, unknown> | undefined => {
  const b = asToolArgs(block);
  if (!b) return undefined;
  if (b.type !== 'tool_use' && b.type !== 'tool_request') return undefined;
  if ((b.name ?? b.tool_name) !== 'ask_user') return undefined;
  const args = b.input ?? b.tool_input;
  if (typeof args === 'string') {
    try { return asToolArgs(JSON.parse(args)); } catch { return undefined; }
  }
  return asToolArgs(args);
};

/** Reconstruct the clarification questions an assistant turn asked from its persisted
 *  `structured_content`, for reload (AIB-6745). */
export const parseAskUserQuestions = (metadata?: string): AskUserQuestion[] | undefined => {
  if (!metadata) return undefined;
  try {
    const blocks: unknown = JSON.parse(metadata)?.structured_content;
    if (!Array.isArray(blocks)) return undefined;
    // Last wins, matching the live SSE path where a second batch replaces the first.
    let args: Record<string, unknown> | undefined;
    for (const block of blocks as unknown[]) args = askUserToolInput(block) ?? args;
    // The meta-options are injected server-side, so they are on whatever the raw args held.
    const questions = normalizeAskUserQuestions(args?.questions).map((q) => ({
      ...q, allowDecideForMe: true, allowFreeText: true,
    }));
    return questions.length > 0 ? questions : undefined;
  } catch { return undefined; }
};

/** Normalise the live `ask_user` SSE payload (questions may arrive as a JSON string). */
export const normalizeAskUserQuestions = (raw: unknown): AskUserQuestion[] => {
  let arr: unknown = raw;
  if (typeof raw === 'string') {
    try { arr = JSON.parse(raw); } catch { return []; }
  }
  if (!Array.isArray(arr)) return [];
  return arr.flatMap((q, i): AskUserQuestion[] => {
    if (!q || typeof q !== 'object') return [];
    const rec = q as Record<string, unknown>;
    const answers = Array.isArray(rec.answers)
      ? rec.answers.filter((a): a is string => typeof a === 'string')
      : [];
    const image = parseQuestionImage(rec);
    if (typeof rec.question !== 'string' || (answers.length === 0 && !image.expectsRegion)) {
      return [];
    }
    return [{
      id: typeof rec.id === 'string' ? rec.id : `q${i + 1}`,
      question: rec.question,
      answers,
      allowDecideForMe: rec.allowDecideForMe !== false,
      allowFreeText: rec.allowFreeText !== false,
      ...image,
    }];
  });
};

/** Read submitted clarification answers off a user message's metadata for reload (AIB-6745). */
export const parseAskUserAnswers = (metadata?: string): AskUserAnswer[] | undefined => {
  if (!metadata) return undefined;
  try {
    const raw = JSON.parse(metadata)?.questionAnswers;
    if (!Array.isArray(raw)) return undefined;
    const answers = raw.flatMap((a): AskUserAnswer[] => {
      if (!a || typeof a !== 'object') return [];
      const answerType = a.answerType;
      if (typeof answerType !== 'string' || !(ASK_USER_ANSWER_TYPES as readonly string[]).includes(answerType)) {
        return [];
      }
      const region = parseRegion(a.region);
      return [{
        question: typeof a.question === 'string' ? a.question : '',
        options: Array.isArray(a.options) ? a.options.filter((o: unknown): o is string => typeof o === 'string') : [],
        answer: typeof a.answer === 'string' ? a.answer : '',
        answerType: answerType as AskUserAnswerType,
        ...(region ? { region } : {}),
      }];
    });
    return answers.length > 0 ? answers : undefined;
  } catch { return undefined; }
};

export const normalizeArtifact = (a: Record<string, unknown>): InlineArtifact | null => {
  const blocked = a.moderationBlocked === true || a.moderationBlocked === 'true';
  if (
    typeof a.artifactId !== 'string' || !a.artifactId ||
    typeof a.name !== 'string' || !a.name ||
    typeof a.contentType !== 'string' || !a.contentType ||
    (!blocked && (typeof a.downloadUrl !== 'string' || !a.downloadUrl))
  ) return null;
  return {
    artifactId: a.artifactId,
    name: a.name,
    contentType: a.contentType,
    downloadUrl: typeof a.downloadUrl === 'string' ? a.downloadUrl : '',
    fileSize: (() => {
      const v = a.sizeBytes;
      let n: number;
      if (typeof v === 'number') {
        n = v;
      } else if (typeof v === 'string') {
        n = Number.parseInt(v, 10);
      } else {
        n = Number.NaN;
      }
      return Number.isFinite(n) && n > 0 ? n : undefined;
    })(),
    insertPosition: (() => {
      const v = a.insertPosition ?? a.insert_position;
      if (typeof v === 'number' && Number.isFinite(v)) return v;
      if (typeof v === 'string') { const n = Number.parseInt(v, 10); return Number.isFinite(n) ? n : undefined; }
      return undefined;
    })(),
    moderationBlocked: blocked || undefined,
  };
};

export const parseInlineArtifacts = (metadata?: string): InlineArtifact[] | undefined => {
  if (!metadata) return undefined;
  try {
    const p = JSON.parse(metadata);
    if (!Array.isArray(p.inline_artifacts)) return undefined;
    const valid = (p.inline_artifacts as Array<Record<string, unknown>>)
      .map(normalizeArtifact)
      .filter((a): a is InlineArtifact => a !== null);
    return valid.length > 0 ? valid : undefined;
  } catch { return undefined; }
};

export const parseInterruption = (metadata?: string): { reason?: string } | undefined => {
  if (!metadata) return undefined;
  try {
    const p = JSON.parse(metadata);
    if (p.interrupted !== true) return undefined;
    return { reason: typeof p.termination_reason === 'string' ? p.termination_reason : undefined };
  } catch { return undefined; }
};

export const precedingUserPrompt = (messages: Message[], messageId: string): string | null => {
  const index = messages.findIndex((m) => m.id === messageId);
  for (let j = index - 1; j >= 0; j--) {
    if (messages[j].role === 'user') return messages[j].content;
  }
  return null;
};

/** Flag the streaming assistant message as interrupted, or add one when nothing streamed yet. */
export const markAssistantInterrupted = (
  msgs: Message[], currentId: string | null, finalId: string, reason?: string,
): Message[] => {
  if (!currentId) {
    return [...msgs, {
      id: finalId, role: 'assistant' as const, content: '', timestamp: new Date(),
      interrupted: true, interruptReason: reason,
    }];
  }
  return msgs.map((m) => m.id === currentId ? {
    ...m, id: finalId, clientId: m.clientId ?? m.id, interrupted: true, interruptReason: reason,
    isStreaming: false, completedAt: new Date(),
  } : m);
};

export const parseAgentBoundaries = (metadata?: string): AgentBoundary[] | undefined => {
  if (!metadata) return undefined;
  try {
    const p = JSON.parse(metadata);
    if (!Array.isArray(p.agent_boundaries)) return undefined;
    const boundaries: AgentBoundary[] = (p.agent_boundaries as Array<Record<string, unknown>>)
      .filter((b) => typeof b.agent === 'string' && typeof b.startPos === 'number')
      .map((b) => ({
        agent: b.agent as string,
        displayName: agentDisplayName(b.agent as string),
        startPos: b.startPos as number,
      }));
    return boundaries.length > 0 ? boundaries : undefined;
  } catch { return undefined; }
};

const parseToolResultMetadata = (metadata?: string): {
  insertPosition?: number;
  success?: boolean;
  imageUrl?: string;
  result?: unknown;
  approvalArgs?: string;
  summary?: string;
  agentName?: string;
} => {
  if (!metadata) return {};
  try {
    const p = JSON.parse(metadata);
    return {
      insertPosition: typeof p.insertPosition === 'number' ? p.insertPosition : undefined,
      success: typeof p.success === 'boolean' ? p.success : undefined,
      imageUrl: typeof p.imageUrl === 'string' ? p.imageUrl : undefined,
      result: p.result ?? undefined,
      approvalArgs: typeof p.approvalArgs === 'string' ? p.approvalArgs : undefined,
      summary: typeof p.summary === 'string' ? p.summary : undefined,
      agentName: typeof p.agentName === 'string' ? p.agentName : undefined,
    };
  } catch {
    return {};
  }
};

// ── Message transforms (backend → frontend) ─────────────────────────────────

/**
 * Build the completed-tool chip for a persisted `tool_result` row from that
 * row's own fields. Returns null for rows that must not render a chip.
 *
 * Resolving from the row rather than a conversation-wide `correlationId` map
 * matters: providers that number tool-call ids per response (Bedrock Mantle's
 * `call_0`) reuse the same id across turns, and a shared map would collapse
 * every one of them onto the last result — rendering that tool's output once
 * per colliding row (AIB-8197).
 */
const parseToolResultRow = (m: BackendMessage): ToolExecution | null => {
  if (m.role !== 'tool_result' || !m.toolName || !m.correlationId) return null;
  if (INTERNAL_TOOLS.has(m.toolName)) return null;
  const meta = parseToolResultMetadata(m.metadata);
  const success = m.success ?? meta.success ?? true;
  const artifactContent = meta.result == null ? m.content : JSON.stringify(meta.result);
  return {
    tool: m.toolName,
    toolUseId: m.correlationId,
    status: 'complete',
    success,
    insertPosition: meta.insertPosition ?? 0,
    artifactInfo: m.toolName === 'present_artifact'
      ? parseArtifactInfo(artifactContent)
      : undefined,
    imageUrl: meta.imageUrl,
    approvalArgs: meta.approvalArgs,
    summary: meta.summary,
    agentName: meta.agentName,
  };
};

const collectToolExecutions = (
  msgs: BackendMessage[],
  startIndex: number,
): ToolExecution[] => {
  const executions: ToolExecution[] = [];
  for (const next of msgs.slice(startIndex + 1)) {
    if (next.role === 'assistant' || next.role === 'user') break;
    const execution = parseToolResultRow(next);
    if (execution) executions.push(execution);
  }
  return executions;
};

const convertNonAssistantMessage = (
  msg: BackendMessage, conversationId: string, index: number,
): Message => {
  const content = msg.content?.replace(/^\u200B$/, '') ?? '';
  return {
    id: msg.messageId || `${conversationId}-${index}`,
    role: msg.role as 'user' | 'assistant' | 'system',
    content: msg.retracted ? '' : content,
    timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
    guardrailRetracted: msg.retracted || undefined,
    askUserAnswers: msg.retracted ? undefined : parseAskUserAnswers(msg.metadata),
    attachments: msg.attachments?.map((att) => ({
      attachmentId: att.attachmentId, filename: att.filename, contentType: att.contentType,
      fileSize: att.fileSize, contextStrategy: att.contextStrategy,
      url: att.url ? `${API_BASE_URL}${att.url}` : null,
      previewUrl: att.previewUrl ? `${API_BASE_URL}${att.previewUrl}` : null,
      previewFileSize: att.previewFileSize ?? null,
      previewName: att.previewName ?? null,
    })),
  };
};

interface AssistantRunAccumulator {
  retracted: boolean;
  interrupted: boolean;
  interruptReason?: string;
  inlineArtifacts: InlineArtifact[];
  agentBoundaries: AgentBoundary[];
  askUserQuestions?: AskUserQuestion[];
  modelSuggestion?: ModelSuggestion;
}

const processAssistantMessage = (
  cur: BackendMessage,
  index: number,
  backendMessages: BackendMessage[],
  mergedContent: string[],
  mergedTools: ToolExecution[],
  acc: AssistantRunAccumulator,
): void => {
  const content = cur.content?.replace(/^\u200B$/, '') ?? '';
  const offset = mergedContent.join('').length;
  if (content) mergedContent.push(content);

  for (const tool of collectToolExecutions(backendMessages, index)) {
    mergedTools.push({ ...tool, insertPosition: offset + (tool.insertPosition ?? 0) });
  }

  if (cur.retracted) acc.retracted = true;
  const interruption = parseInterruption(cur.metadata);
  if (interruption) {
    acc.interrupted = true;
    acc.interruptReason = interruption.reason;
  }

  const askUserQuestions = parseAskUserQuestions(cur.metadata);
  if (askUserQuestions) acc.askUserQuestions = askUserQuestions;

  const modelSuggestion = parseModelSuggestion(cur.metadata);
  if (modelSuggestion) acc.modelSuggestion = modelSuggestion;

  const artifacts = parseInlineArtifacts(cur.metadata);
  if (artifacts) acc.inlineArtifacts.push(...artifacts);

  const boundaries = parseAgentBoundaries(cur.metadata);
  if (boundaries) {
    acc.agentBoundaries.push(
      ...boundaries.map((b) => ({ ...b, startPos: offset + b.startPos })),
    );
  }
};

const mergeAssistantRun = (
  backendMessages: BackendMessage[],
  startIndex: number,
): { message: BackendMessage; endIndex: number; mergedContent: string; mergedTools: ToolExecution[]; retracted: boolean; interrupted: boolean; interruptReason?: string; inlineArtifacts: InlineArtifact[]; agentBoundaries: AgentBoundary[]; askUserQuestions?: AskUserQuestion[]; modelSuggestion?: ModelSuggestion; completedAt?: string } => {
  const mergedContent: string[] = [];
  const mergedTools: ToolExecution[] = [];
  let completedAt: string | undefined;
  const acc: AssistantRunAccumulator = {
    retracted: !!backendMessages[startIndex].retracted,
    interrupted: false,
    inlineArtifacts: [],
    agentBoundaries: [],
  };
  let i = startIndex;

  while (i < backendMessages.length) {
    const cur = backendMessages[i];
    if (cur.role !== 'assistant' && cur.role !== 'tool_result') break;
    completedAt = cur.createdAt ?? completedAt;
    if (cur.role === 'tool_result') { i++; continue; }

    processAssistantMessage(cur, i, backendMessages, mergedContent, mergedTools, acc);
    i++;
    // An interrupted assistant message ends the run.  Anything after it is
    // a fresh retry turn and must not be folded into the same message
    // (otherwise the InterruptedNotice would render in the middle of new
    // content).  See AIB-6718.
    if (acc.interrupted) break;
  }

  return { message: backendMessages[startIndex], endIndex: i, mergedContent: mergedContent.join(''), mergedTools, retracted: acc.retracted, interrupted: acc.interrupted, interruptReason: acc.interruptReason, inlineArtifacts: acc.inlineArtifacts, agentBoundaries: acc.agentBoundaries, askUserQuestions: acc.askUserQuestions, modelSuggestion: acc.modelSuggestion, completedAt };
};

const nonEmptyOrUndefined = <T,>(arr: T[]): T[] | undefined => arr.length > 0 ? arr : undefined;

export const mergeToolExecutions = (
  existing: ToolExecution[] | undefined,
  incoming: ToolExecution[] | undefined,
): ToolExecution[] | undefined => {
  if (!incoming?.length) return existing;
  if (!existing?.length) return incoming;
  const existingIndexById = new Map<string, number>(
    existing.flatMap((t, i) => (t.toolUseId ? [[t.toolUseId, i]] : [])),
  );
  const merged = [...existing];
  for (const t of incoming) {
    if (t.toolUseId && existingIndexById.has(t.toolUseId)) {
      const idx = existingIndexById.get(t.toolUseId)!;
      const kept = merged[idx];
      // Prefer the incoming update for status/success/approval fields so a completed
      // payload wins over a cached "executing" chip, but never clobber existing
      // metadata (summary/agentName/artifactInfo/imageUrl) with incoming undefined —
      // reconnect payloads sometimes omit these even though we already streamed them.
      merged[idx] = {
        ...kept,
        ...t,
        summary: t.summary ?? kept.summary,
        agentName: t.agentName ?? kept.agentName,
        artifactInfo: t.artifactInfo ?? kept.artifactInfo,
        imageUrl: t.imageUrl ?? kept.imageUrl,
        insertPosition: kept.insertPosition ?? t.insertPosition,
      };
    } else {
      if (t.toolUseId) {
        existingIndexById.set(t.toolUseId, merged.length);
      }
      merged.push(t);
    }
  }
  if (merged.some((t) => t.insertPosition != null)) {
    merged.sort((a, b) => {
      if (a.insertPosition == null && b.insertPosition == null) return 0;
      if (a.insertPosition == null) return 1;
      if (b.insertPosition == null) return -1;
      return a.insertPosition - b.insertPosition;
    });
  }
  return merged.length > 0 ? merged : undefined;
};

export const appendArtifactToMessage = (m: Message, targetId: string, artifact: InlineArtifact): Message => {
  if (m.id !== targetId) return m;
  const existing = m.inlineArtifacts ?? [];
  if (existing.some((a) => a.artifactId === artifact.artifactId)) return m;
  return { ...m, inlineArtifacts: [...existing, artifact] };
};

/**
 * Returns true when the browser has cached an insufficientCredits message for
 * the given conversation, so the online-reconnect guard can skip retrying.
 * Pure module-level helper — no hook closure needed.
 */
const hasCachedInsufficientCredits = (data: InfiniteData<MessagePage> | undefined): boolean =>
  data?.pages.some((p) => p.messages.some((m) => Boolean(m.insufficientCredits))) ?? false;

/**
 * AIB-8372: a live SSE frame writing to an empty cache seeds a synthetic page
 * (`hasMore: false`) that hides the real history and kills load-more, and a page-0
 * fetch resolving mid-stream replaces the live message the stream is writing to.
 * Reconnecting is only safe once page 0 is cached and no page-0 fetch is in flight.
 */
export const isReconnectSafe = (
  state: QueryState<InfiniteData<MessagePage>> | undefined,
): boolean => (state?.data?.pages.length ?? 0) > 0 && state?.fetchStatus === 'idle';

const withOptimisticUserMessage = (
  old: InfiniteData<MessagePage> | undefined,
  userMsg: Message,
): InfiniteData<MessagePage> => {
  if (!old) {
    return {
      pages: [{ messages: [userMsg], hasMore: false, oldestTimestamp: null }],
      pageParams: [undefined],
    };
  }
  return {
    ...old,
    pages: old.pages.map((p, i) => i === 0 ? { ...p, messages: [...p.messages, userMsg] } : p),
  };
};

/**
 * Waits for a page-0 fetch that is already in flight, so a send can let it land
 * instead of reverting it with `cancelQueries` and losing everything it was
 * bringing in (AIB-8372). Paused fetches are left alone: offline, one would
 * never settle.
 */
const settlePendingHistory = async (queryClient: QueryClient, key: QueryKey): Promise<void> => {
  const query = queryClient.getQueryCache().find({ queryKey: key });
  if (query?.state.fetchStatus !== 'fetching' || !query.promise) return;

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      query.promise.catch(() => {}),
      new Promise((resolve) => { timer = setTimeout(resolve, HISTORY_SETTLE_BUDGET_MS); }),
    ]);
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Returns true when consecutive same-agent boundaries can be merged.
 * The trailing "os" span between them must be empty: no visible text, no tool
 * chips, and no artifact markers in the range [last.startPos, startPos).
 */
export const shouldMergeConsecutiveAgent = (
  boundaries: AgentBoundary[],
  agentName: string,
  latestText: string,
  startPos: number,
  tools?: ToolExecution[],
  artifacts?: InlineArtifact[],
): boolean => {
  if (boundaries.length < 2) return false;
  const last = boundaries.at(-1);
  const prev = boundaries.at(-2);
  if (last?.agent !== ORCHESTRATOR_AGENT || prev?.agent !== agentName) return false;
  const osStart = last.startPos;
  if (latestText.substring(osStart, startPos).trim()) return false;
  if (tools?.some((t) => { const pos = t.insertPosition ?? 0; return pos >= osStart && pos < startPos; })) return false;
  if (artifacts?.some((a) => a.insertPosition != null && a.insertPosition >= osStart && a.insertPosition < startPos)) return false;
  return true;
};

type RawCompletedTool = {
  toolUseId: string;
  tool: string;
  success: boolean;
  insertPosition?: number;
  agentName?: string;
  summary?: string;
};
type RawExecutingTool = { toolUseId: string; tool: string; insertPosition?: number; agentName?: string; summary?: string };

/**
 * Transition any cached "executing" tool chip to "complete" when the backend's
 * reconnect payload lists it in completedTools. Pure — no hook closure needed.
 */
export const reconciledTools = (
  existing: ToolExecution[] | undefined,
  completedMap: Map<string, boolean>,
): ToolExecution[] | undefined => {
  if (!existing) return undefined;
  return existing.map((t) => {
    if (t.status !== 'executing') return t;
    if (t.toolUseId && completedMap.has(t.toolUseId)) {
      return { ...t, status: 'complete' as const, success: completedMap.get(t.toolUseId) };
    }
    return t;
  });
};

/**
 * Build the initial set of ToolExecution entries from a reconnect payload.
 * Positions are adjusted by `offset` (the persisted text prefix length).
 */
const seedCompleted = (rawCompleted: RawCompletedTool[], offset: number): ToolExecution[] =>
  rawCompleted
    .filter((ct) => !INTERNAL_TOOLS.has(ct.tool))
    .map((ct) => ({
      tool: ct.tool, toolUseId: ct.toolUseId,
      status: 'complete' as const, success: ct.success,
      insertPosition: ct.insertPosition == null ? undefined : ct.insertPosition + offset,
      agentName: ct.agentName,
      summary: ct.summary,
    }));

const seedExecuting = (rawExecuting: RawExecutingTool[], offset: number): ToolExecution[] =>
  rawExecuting
    .filter((et) => !INTERNAL_TOOLS.has(et.tool))
    .map((et) => ({
      tool: et.tool, toolUseId: et.toolUseId,
      status: 'executing' as const,
      insertPosition: et.insertPosition == null ? undefined : et.insertPosition + offset,
      agentName: et.agentName,
      summary: et.summary,
    }));

export const seedToolExecutions = (
  rawCompleted: RawCompletedTool[] | undefined,
  rawExecuting: RawExecutingTool[] | undefined,
  offset: number,
): ToolExecution[] | undefined => {
  const seeded: ToolExecution[] = [
    ...(rawCompleted ? seedCompleted(rawCompleted, offset) : []),
    ...(rawExecuting ? seedExecuting(rawExecuting, offset) : []),
  ];
  return seeded.length > 0 ? seeded : undefined;
};

/**
 * Merge consecutive boundaries for the same non-"os" agent.
 *
 * Only merges when the intervening "os" span is truly empty (no visible text,
 * tools, or artifacts). This prevents pulling orchestrator content into an
 * agent block.
 */
const isOsSpanEmpty = (
  content: string,
  tools: ToolExecution[] | undefined,
  artifacts: InlineArtifact[] | undefined,
  osStart: number,
  osEnd: number,
): boolean => {
  if (content.substring(osStart, osEnd).trim()) return false;
  if (tools?.some((t) => { const pos = t.insertPosition ?? 0; return pos >= osStart && pos < osEnd; })) return false;
  if (artifacts?.some((a) => a.insertPosition != null && a.insertPosition >= osStart && a.insertPosition < osEnd)) return false;
  return true;
};

export const mergeSameAgentBoundaries = (
  boundaries: AgentBoundary[],
  content: string,
  tools: ToolExecution[] | undefined,
  artifacts: InlineArtifact[] | undefined,
): AgentBoundary[] => {
  if (boundaries.length < 2) return boundaries;
  const sorted = [...boundaries].sort((a, b) => a.startPos - b.startPos);
  const merged: AgentBoundary[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = merged.at(-1)!;
    const cur = sorted[i];

    if (cur.agent !== ORCHESTRATOR_AGENT && prev.agent === cur.agent) continue;

    if (cur.agent !== ORCHESTRATOR_AGENT && prev.agent !== ORCHESTRATOR_AGENT) {
      merged.push(cur);
      continue;
    }

    if (prev.agent !== ORCHESTRATOR_AGENT && cur.agent === ORCHESTRATOR_AGENT) {
      const nextNonOs = sorted.slice(i + 1).find((b) => b.agent !== ORCHESTRATOR_AGENT);
      if (nextNonOs?.agent === prev.agent && isOsSpanEmpty(content, tools, artifacts, cur.startPos, nextNonOs.startPos)) {
        continue;
      }
    }

    merged.push(cur);
  }

  return merged;
};

const canMergeAssistantMessages = (prev: Message, cur: Message): boolean =>
  prev.role === 'assistant' && cur.role === 'assistant'
  && !prev.guardrailRetracted && !cur.guardrailRetracted
  && !prev.interrupted && !cur.interrupted
  && !prev.insufficientCredits && !cur.insufficientCredits;

export const deduplicateArtifacts = (artifacts: InlineArtifact[]): InlineArtifact[] => {
  const seen = new Set<string>();
  return artifacts.filter((a) => {
    if (seen.has(a.artifactId)) return false;
    seen.add(a.artifactId);
    return true;
  });
};

const mergeAdjacentAssistant = (prev: Message, cur: Message): Message => {
  const prevLen = prev.content.length;
  const mergedContent = prev.content + cur.content;
  const mergedTools = [
    ...(prev.toolExecutions || []),
    ...(cur.toolExecutions || []).map((t) => ({
      ...t,
      insertPosition: prevLen + (t.insertPosition ?? 0),
    })),
  ];
  const mergedArtifacts = deduplicateArtifacts([
    ...(prev.inlineArtifacts ?? []),
    ...(cur.inlineArtifacts ?? []).map((a) => ({
      ...a,
      insertPosition: a.insertPosition == null ? undefined : prevLen + a.insertPosition,
    })),
  ]);
  const rawBoundaries = [
    ...(prev.agentBoundaries ?? []),
    ...(cur.agentBoundaries ?? []).map((b) => ({
      ...b,
      startPos: prevLen + b.startPos,
    })),
  ];
  const mergedBoundaries = mergeSameAgentBoundaries(
    rawBoundaries,
    mergedContent,
    mergedTools.length > 0 ? mergedTools : undefined,
    mergedArtifacts.length > 0 ? mergedArtifacts : undefined,
  );
  return {
    ...prev,
    content: mergedContent,
    completedAt: cur.completedAt ?? prev.completedAt,
    toolExecutions: mergedTools.length > 0 ? mergedTools : undefined,
    modelSuggestion: cur.modelSuggestion ?? prev.modelSuggestion,
    askUserQuestions: cur.askUserQuestions ?? prev.askUserQuestions,
    inlineArtifacts: mergedArtifacts.length > 0 ? mergedArtifacts : undefined,
    agentBoundaries: mergedBoundaries.length > 0 ? mergedBoundaries : undefined,
  };
};

/**
 * Merge consecutive assistant messages that were split across page boundaries.
 * When pagination cuts in the middle of an assistant turn, each page independently
 * merges what it can, but the tail of one page and the head of the next remain
 * separate.  This pass stitches them back together.
 */
const mergeConsecutiveAssistantMessages = (msgs: Message[]): Message[] => {
  if (msgs.length < 2) return msgs;
  const result: Message[] = [msgs[0]];
  for (let i = 1; i < msgs.length; i++) {
    const prev = result.at(-1)!;
    const cur = msgs[i];
    if (canMergeAssistantMessages(prev, cur)) {
      result[result.length - 1] = mergeAdjacentAssistant(prev, cur);
    } else {
      result.push(cur);
    }
  }
  return result;
};

const buildAssistantMessage = (
  run: ReturnType<typeof mergeAssistantRun>, conversationId: string, endIndex: number,
): Message => ({
  id: run.message.messageId || `${conversationId}-merged-${endIndex}`,
  role: 'assistant',
  content: run.retracted ? '' : run.mergedContent,
  timestamp: run.message.createdAt ? new Date(run.message.createdAt) : new Date(),
  completedAt: run.completedAt ? new Date(run.completedAt) : undefined,
  guardrailRetracted: run.retracted || undefined,
  interrupted: run.interrupted || undefined,
  interruptReason: run.interruptReason,
  toolExecutions: run.retracted ? undefined : nonEmptyOrUndefined(run.mergedTools),
  modelSuggestion: run.retracted ? undefined : run.modelSuggestion,
  askUserQuestions: run.retracted ? undefined : run.askUserQuestions,
  feedbackRating: run.message.feedbackRating,
  inlineArtifacts: run.retracted ? undefined : nonEmptyOrUndefined(run.inlineArtifacts),
  agentBoundaries: run.retracted ? undefined : nonEmptyOrUndefined(run.agentBoundaries),
});

const transformMessages = (backendMessages: BackendMessage[], conversationId: string): Message[] => {
  const messages: Message[] = [];

  let i = 0;
  while (i < backendMessages.length) {
    const msg = backendMessages[i];

    if (msg.role === 'tool_result') { i++; continue; }

    if (msg.role !== 'assistant') {
      messages.push(convertNonAssistantMessage(msg, conversationId, i));
      i++;
      continue;
    }

    const run = mergeAssistantRun(backendMessages, i);
    i = run.endIndex;
    messages.push(buildAssistantMessage(run, conversationId, i));
  }
  return messages;
};

const fetchMessagePage = async (conversationId: string, before?: string): Promise<MessagePage> => {
  const params = new URLSearchParams({ size: String(PAGE_SIZE) });
  if (before) params.set('before', before);
  const { data } = await client.get<PaginatedMessagesResponse>(
    `/api/v1/conversations/${conversationId}/messages?${params.toString()}`,
  );
  return {
    messages: transformMessages(data.messages || [], conversationId),
    hasMore: data.hasMore,
    oldestTimestamp: data.messages.length > 0 ? data.messages[0].createdAt ?? null : null,
  };
};

const uploadFiles = async (
  files: File[],
  currentConversationId: string | null,
): Promise<{ attachmentIds: string[]; optimisticAttachments: MessageAttachment[]; conversationId: string }> => {
  const token = getAuthToken();
  if (!token) throw new Error('Authentication required. Please log in.');

  let convId = currentConversationId;
  if (!convId) {
    // Server generates the conversation ID — the client never proposes one.
    const res = await client.post<{ conversationId: string }>('/api/v1/conversations', {});
    convId = res.data.conversationId;
  }

  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));

  let data: BackendAttachment[];
  try {
    const res = await client.post<BackendAttachment[]>(
      `/api/v1/conversations/${convId}/attachments`, formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    data = res.data;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 413) {
      throw new Error('File too large. Maximum upload size is 500 MB.');
    }
    if (status === 422) {
      const errData = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      throw new Error((errData?.message as string) ?? 'This image could not be uploaded.');
    }
    throw err;
  }
  return {
    attachmentIds: data.map((a) => a.attachmentId),
    optimisticAttachments: data.map((a) => ({
      attachmentId: a.attachmentId, filename: a.filename, contentType: a.contentType,
      fileSize: a.fileSize, contextStrategy: a.contextStrategy,
      url: a.url ? `${API_BASE_URL}${a.url}` : null,
      previewUrl: a.previewUrl ? `${API_BASE_URL}${a.previewUrl}` : null,
      previewFileSize: a.previewFileSize ?? null,
      previewName: a.previewName ?? null,
    })),
    conversationId: convId,
  };
};

// ── Pure helpers for SSE event processing ────────────────────────────────────

const appendToolExecution = (msg: Message, newExec: ToolExecution): Message => {
  if (newExec.toolUseId && msg.toolExecutions?.some((t) => t.toolUseId === newExec.toolUseId)) return msg;
  return { ...msg, toolExecutions: [...(msg.toolExecutions || []), newExec] };
};

/**
 * Fold a settled skill load into the message, or append it when there is no
 * entry yet. A reconnect mid-load seeds an `executing` entry from a payload
 * whose `summary` and `insertPosition` are both optional, so the setup step
 * stays the fallback for the skill id and the offset.
 */
const completeSkillLoad = (msg: Message, settled: ToolExecution): Message => {
  const tools = msg.toolExecutions;
  if (!tools?.some((t) => t.toolUseId === settled.toolUseId)) {
    return appendToolExecution(msg, settled);
  }
  return {
    ...msg,
    toolExecutions: tools.map((t) => (t.toolUseId === settled.toolUseId
      ? {
        ...t,
        status: settled.status,
        success: settled.success,
        summary: t.summary ?? settled.summary,
        insertPosition: t.insertPosition ?? settled.insertPosition,
      }
      : t)),
  };
};

const markToolComplete = (
  msg: Message, toolName: string, toolUseId: string | undefined,
  success: boolean, artifactInfo?: ArtifactInfo, imageUrl?: string,
): Message => {
  if (!msg.toolExecutions) return msg;
  let matched = false;
  const updated = msg.toolExecutions.map((t) => {
    if (matched) return t;
    const isMatch = toolUseId ? t.toolUseId === toolUseId : t.tool === toolName && t.status === 'executing';
    if (!isMatch) return t;
    matched = true;
    return { ...t, status: 'complete' as const, success, artifactInfo, imageUrl };
  });
  return { ...msg, toolExecutions: updated };
};

/**
 * Transition any `pending_approval` chip for the given tool to `executing`.
 * Returns whether a transition occurred so the caller can skip creating a new chip.
 */
const applyPendingToExecuting = (
  execs: ToolExecution[] | undefined,
  toolName: string,
  toolUseId: string | undefined,
): { execs: ToolExecution[] | undefined; transitioned: boolean } => {
  let transitioned = false;
  const result = execs?.map((t) => {
    const isTarget = (toolUseId ? t.toolUseId === toolUseId : t.tool === toolName) && t.status === 'pending_approval';
    if (!isTarget) return t;
    transitioned = true;
    return { ...t, status: 'executing' as const };
  });
  return { execs: result, transitioned };
};

const applyExecutingToPendingApproval = (
  execs: ToolExecution[] | undefined,
  toolName: string,
  toolUseId: string | undefined,
  approvalArgs: string | undefined,
  summary: string | undefined,
): { execs: ToolExecution[] | undefined; transitioned: boolean } => {
  let transitioned = false;
  const result = execs?.map((t) => {
    const isTarget = (toolUseId ? t.toolUseId === toolUseId : t.tool === toolName) && t.status === 'executing';
    if (!isTarget) return t;
    transitioned = true;
    return {
      ...t,
      status: 'pending_approval' as const,
      approvalArgs,
      ...(summary === undefined ? {} : { summary }),
    };
  });
  return { execs: result, transitioned };
};

/** Return updated tool executions with the matched entry marked as rejected. */
const applyToolRejected = (
  execs: ToolExecution[] | undefined,
  toolName: string,
  toolUseId: string | undefined,
): ToolExecution[] | undefined =>
  execs?.map((t) => {
    const matches = toolUseId
      ? t.toolUseId === toolUseId
      : (t.tool === toolName && t.status === 'pending_approval');
    return matches ? { ...t, status: 'complete' as const, success: false } : t;
  });

/**
 * Returns true when any message already contains a pending-approval chip for the
 * given tool.  Used to deduplicate tool_approval_request SSE events that can be
 * emitted more than once (reconnect replay, monitoring-loop checkpoint replay).
 *
 * Extracted to module scope so it does not contribute to nesting depth inside the
 * hook and can be called without capturing hook-internal references.
 */
const hasPendingApprovalAnywhere = (
  msgs: Message[],
  toolName: string,
  toolUseId: string | undefined,
): boolean =>
  msgs.some((m) =>
    m.toolExecutions?.some((t) =>
      t.status === 'pending_approval' && (toolUseId ? t.toolUseId === toolUseId : t.tool === toolName),
    ),
  );

// ── Feedback cache helpers (module-level to avoid deep nesting) ───────────────

const applyFeedbackRating = (messageId: string, rating: 'up' | 'down') =>
  (old: InfiniteData<MessagePage> | undefined): InfiniteData<MessagePage> | undefined => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map((p) => ({
        ...p,
        messages: p.messages.map((m) => m.id === messageId ? { ...m, feedbackRating: rating } : m),
      })),
    };
  };

const clearFeedbackRating = (messageId: string) =>
  (old: InfiniteData<MessagePage> | undefined): InfiniteData<MessagePage> | undefined => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map((p) => ({
        ...p,
        messages: p.messages.map((m) => m.id === messageId ? { ...m, feedbackRating: undefined } : m),
      })),
    };
  };

function updateExecutingToolArgs(
  toolExecutions: ToolExecution[],
  toolUseId: string | undefined,
  argsJson: string | undefined,
  summary: string | undefined,
): { updated: ToolExecution[]; found: boolean } {
  let found = false;
  const updated = toolExecutions.map((t) => {
    if (t.toolUseId !== toolUseId || t.status !== 'executing') return t;
    found = true;
    return {
      ...t,
      approvalArgs: argsJson,
      ...(summary === undefined ? {} : { summary }),
    };
  });
  return { updated, found };
}

/** Check whether a tool_start with the given toolUseId already exists in a cached
 *  message other than the current streaming message (dedup on reconnect). */
function isToolDuplicateInCache(
  toolUseId: string,
  currentMid: string | null | undefined,
  cache: InfiniteData<MessagePage> | undefined,
): boolean {
  if (!cache) return false;
  return cache.pages.some((p) =>
    p.messages.some((m) =>
      m.id !== currentMid && m.toolExecutions?.some((t) => t.toolUseId === toolUseId),
    ),
  );
}

/** Attempt a fetch; if 401 or 403, refresh the auth token and retry once. */
async function fetchWithTokenRefresh(
  doFetch: (token: string) => Promise<Response>,
  token: string,
): Promise<Response> {
  const response = await doFetch(await tokenLasting(token));
  if (response.status === 401 || response.status === 403) {
    const refreshed = await refreshAuthToken();
    if (refreshed) return doFetch(refreshed);
  }
  return response;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export interface UseChatOptions {
  /**
   * Takes a screenshot of the screen around the chat for each turn the user
   * sends. It rides on that request only: it is not uploaded, stored or shown.
   */
  snapshot?: () => Promise<ViewSnapshot | null>;
  /** The assistant asked, mid-turn, to see the screen around the chat; answering is the screen's job. */
  onPreviewCapture?: (captureId: string) => void;
}

export const useChat = (conversationId: string | null, { snapshot, onPreviewCapture }: UseChatOptions = {}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { prependConversation, updateConversation, fetchConversations } = useConversations();

  // ── State ──────────────────────────────────────────────────────────────
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [resolvedConversationId, setResolvedConversationId] = useState<string | null>(null);
  const [setupSteps, setSetupSteps] = useState<SetupStep[]>([]);
  const [setupExiting, setSetupExiting] = useState(false);
  const [compacting, setCompacting] = useState(false);
  const [pendingModelSuggestion, setPendingModelSuggestion] =
    useState<ModelSuggestion | null>(null);
  const [pendingAskUser, setPendingAskUser] = useState<PendingAskUser | null>(null);
  const [isReconnecting, setIsReconnecting] = useState(false);

  // ── Refs ───────────────────────────────────────────────────────────────
  const conversationIdRef = useRef<string | null>(conversationId);
  const currentAssistantTextRef = useRef('');
  const assistantMessageIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef<string | null>(null);
  /** Tracks the original user message text for conversationId resolution */
  const userMessageRef = useRef('');
  /** Set to the resolved conversationId while an SSE stream is actively reading.
   *  The reset effect checks this to avoid killing a live stream during the
   *  null→realId URL transition. */
  const activeStreamConvIdRef = useRef<string | null>(null);
  /** rAF handle for throttled text-chunk flushing (0 = none pending). */
  const rafFlushRef = useRef<number>(0);
  /** Mirrors `setupSteps` so SSE handlers can upsert without stale-closure reads. */
  const setupStepsRef = useRef<SetupStep[]>([]);
  const setupExitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * AbortController for the currently-running attemptReconnect call.
   * sendMessage aborts this before starting its own stream, preventing
   * the stale reconnect from calling invalidateQueries / setIsSending(false)
   * while the new send is in flight.
   */
  const reconnectAbortRef = useRef<AbortController | null>(null);
  /** Bumped on every conversation switch, so async work started before it can tell it is stale. */
  const conversationEpochRef = useRef(0);
  /**
   * Handle for the debounced post-stream `invalidateQueries` scheduled by
   * `stopStreamingForMessage` (AIB-6610). Held so we can cancel it when a
   * new send / reconnect / conversation switch / unmount starts, preventing
   * the refetch from clobbering live stream state.
   */
  const streamSyncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stoppedTurnResyncRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failedUploadMsgIdRef = useRef<string | null>(null);
  const onPreviewCaptureRef = useRef(onPreviewCapture);
  useEffect(() => {
    onPreviewCaptureRef.current = onPreviewCapture;
  }, [onPreviewCapture]);
  const snapshotRef = useRef(snapshot);
  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);
  /**
   * Snapshot of the most recent send-options used so retryAfterInterruption
   * can replay with the same runner / approval mode / disabled agents / mode
   * rather than collapsing to defaults.  We deliberately omit
   * `files` from the replay -- the original files are already attached to
   * the persisted user message and the LLM sees them in history; re-uploading
   * would create duplicate attachments.  Across page refreshes this ref is
   * empty and retry falls back to defaults, which matches what the picker UI
   * shows after a refresh anyway.
   */
  const lastSendOptionsRef = useRef<{
    mode?: string;
    runnerType?: string;
    approvalMode?: string;
  }>({});
  /** Always reflects the current user?.id without requiring sendMessage re-memoization. */
  const userIdRef = useRef<string | undefined>(user?.id);
  useEffect(() => { userIdRef.current = user?.id; }, [user?.id]);

  // AIB-6610: on unmount, cancel any pending post-stream invalidate so it
  // cannot fire against a torn-down hook / stale conversation.
  useEffect(() => () => {
    if (streamSyncTimeoutRef.current) {
      clearTimeout(streamSyncTimeoutRef.current);
      streamSyncTimeoutRef.current = null;
    }
    if (stoppedTurnResyncRef.current) {
      clearTimeout(stoppedTurnResyncRef.current);
      stoppedTurnResyncRef.current = null;
    }
  }, []);

  // ── Query cache helper ─────────────────────────────────────────────────
  const updateMessages = useCallback((updater: (msgs: Message[]) => Message[]) => {
    const key = conversationQueryKey(conversationIdRef.current);
    queryClient.setQueryData<InfiniteData<MessagePage>>(key, (old) => {
      if (!old) {
        return { pages: [{ messages: updater([]), hasMore: false, oldestTimestamp: null }], pageParams: [undefined] };
      }
      return { ...old, pages: old.pages.map((p, i) => i === 0 ? { ...p, messages: updater(p.messages) } : p) };
    });
  }, [queryClient]);

  // ── Resolve conversation ID from SSE event data ──────────────────────
  function resolveConversationId(data: Record<string, unknown>) {
    if (!data.conversationId) return;
    const convId = data.conversationId as string;
    const isNew = !conversationIdRef.current;
    if (isNew) {
      const cur = queryClient.getQueryData<InfiniteData<MessagePage>>(conversationQueryKey(null));
      if (cur) {
        queryClient.setQueryData(conversationQueryKey(convId), cur);
        queryClient.removeQueries({ queryKey: conversationQueryKey(null) });
      }
      prependConversation({
        conversationId: convId,
        title: userMessageRef.current.substring(0, 50) + (userMessageRef.current.length > 50 ? '...' : ''),
        createdAt: new Date().toISOString(),
        lastActivityAt: new Date().toISOString(),
        messageCount: 1,
      });
      // Kick off a fresh server fetch immediately after the optimistic update.
      // This supersedes any stale background refetch that was in flight before
      // the conversation existed, preventing it from overwriting our new entry.
      fetchConversations();
    } else {
      updateConversation(convId, { lastActivityAt: new Date().toISOString() });
    }
    conversationIdRef.current = convId;
    activeStreamConvIdRef.current = convId;
    setResolvedConversationId(convId);
  }

  // ── SSE event handler (called by useSseStream via ref — no dep chains) ─
  const handleSseEvent = useCallback((event: SseEvent) => {
    const { type, data } = event;

    // Guard: ignore events from a stale conversation
    if (conversationIdRef.current !== null && data.conversationId && data.conversationId !== conversationIdRef.current) return;

    resolveConversationId(data);
    if (data.requestId) requestIdRef.current = data.requestId as string;

    dispatchSseEvent(type, data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, updateMessages]);

  /** Route an SSE event type to the appropriate handler. */
  function dispatchSseEvent(type: string, data: Record<string, unknown>) {
    switch (type) {
      case 'heartbeat': case 'cancelled': return;
      case 'done': {
        // Terminal event: clear the compacting banner even if `compacting_done`
        // was dropped, so it can never stick past the end of a turn.
        setCompacting(false);
        // Flush any buffered text immediately when the stream signals completion.
        // requestAnimationFrame stops firing in hidden tabs, so if the user switched
        // tabs while streaming, the rAF callback may never have run.  Flushing here
        // (inside the event handler, before readStream's while-loop exits) guarantees
        // content lands in the React Query cache even if the tab is currently hidden.
        if (rafFlushRef.current !== 0) {
          cancelAnimationFrame(rafFlushRef.current);
          flushTextToCache();
        }
        // Replace the fake timestamp ID with the real DB message ID so the
        // ActionsBar can submit feedback immediately without a refetch.
        const realMessageId = data.messageId as string | undefined;
        const mid = assistantMessageIdRef.current;
        if (mid) {
          const finalId = realMessageId ?? mid;
          updateMessages((msgs) => msgs.map((m) =>
            m.id === mid ? { ...m, id: finalId, clientId: m.clientId ?? m.id, completedAt: new Date() } : m,
          ));
          assistantMessageIdRef.current = finalId;
        }
        return;
      }
      case 'server_shutdown': throw new ServerShutdownError(
        typeof data.retryForMs === 'number' ? data.retryForMs : undefined,
      );
      case 'environment_allocating': handleEnvAllocating(); return;
      case 'environment_ready': handleEnvReady(); return;
      case 'model_suggestion': handleModelSuggestion(data); return;
      case 'ask_user': handleAskUser(data); return;
      case 'guardrail_violation': handleGuardrail(data); return;
      case 'interrupted': handleInterrupted(data); return;
      case 'reconnected': handleReconnected(data); return;
      case 'tool_start':
      case 'tool_executing': handleToolStart(data); return;
      case 'tool_complete': handleToolComplete(data); return;
      case 'tool_approval_request': handleToolApprovalRequest(data); return;
      case 'agent_context': handleAgentContext(data); return;
      case 'tool_rejected': handleToolRejected(data); return;
      case 'insufficient_credits': handleInsufficientCredits(data); return;
      case 'inline_artifact': handleInlineArtifact(data); return;
      case 'conversation_title': handleConversationTitle(data); return;
      case 'compacting': handleCompacting(true); return;
      case 'compacting_done': handleCompacting(false); return;
      case 'preview_capture': handlePreviewCapture(data); return;
      default: handleDefaultEvent(data); return;
    }
  }

  function handlePreviewCapture(data: Record<string, unknown>) {
    if (typeof data.captureId === 'string') onPreviewCaptureRef.current?.(data.captureId);
  }

  function handleCompacting(active: boolean) {
    setCompacting(active);
  }

  function handleConversationTitle(data: Record<string, unknown>) {
    const title = data.title as string | undefined;
    const convId = data.conversationId as string | undefined;
    if (title && convId) {
      updateConversation(convId, { title });
    }
  }

  const applySetupSteps = useCallback((next: SetupStep[]) => {
    setupStepsRef.current = next;
    setSetupSteps(next);
  }, []);

  const cancelSetupExit = useCallback(() => {
    if (!setupExitTimerRef.current) return;
    clearTimeout(setupExitTimerRef.current);
    setupExitTimerRef.current = null;
  }, []);

  /** Append a step, or merge into the existing one with the same id. */
  const upsertSetupStep = useCallback((step: SetupStep) => {
    cancelSetupExit();
    setSetupExiting(false);
    const prev = setupStepsRef.current;
    const idx = prev.findIndex((s) => s.id === step.id);
    if (idx === -1) {
      applySetupSteps([...prev, step]);
      return;
    }
    const next = [...prev];
    next[idx] = { ...next[idx], ...step };
    applySetupSteps(next);
  }, [applySetupSteps, cancelSetupExit]);

  const removeSetupStep = useCallback((id: string) => {
    applySetupSteps(setupStepsRef.current.filter((s) => s.id !== id));
  }, [applySetupSteps]);

  /** Drop the setup block instantly — for conversation switches, where a fade would trail. */
  const resetSetup = useCallback(() => {
    cancelSetupExit();
    setSetupExiting(false);
    applySetupSteps([]);
  }, [applySetupSteps, cancelSetupExit]);

  /** End-of-turn: let the block animate out, then drop it. */
  const dismissSetup = useCallback(() => {
    if (setupStepsRef.current.length === 0 || setupExitTimerRef.current) return;
    setSetupExiting(true);
    setupExitTimerRef.current = setTimeout(() => {
      setupExitTimerRef.current = null;
      setSetupExiting(false);
      applySetupSteps([]);
    }, SETUP_EXIT_MS);
  }, [applySetupSteps]);

  useEffect(() => cancelSetupExit, [cancelSetupExit]);

  function handleEnvAllocating() {
    ensureAssistantMessage();
    // Re-emitted allocating events must not restart the elapsed timer.
    const started = setupStepsRef.current.find((s) => s.id === 'environment')?.startedAt;
    upsertSetupStep({
      id: 'environment', kind: 'environment', status: 'active', startedAt: started ?? Date.now(),
    });
  }

  function handleEnvReady() {
    ensureAssistantMessage();
    upsertSetupStep({ id: 'environment', kind: 'environment', status: 'done' });
  }

  function handleModelSuggestion(data: Record<string, unknown>) {
    const suggestion = normalizeModelSuggestion(data);
    if (!suggestion) return;
    setPendingModelSuggestion(suggestion);
    attachModelSuggestion(suggestion);
  }

  function attachModelSuggestion(suggestion: ModelSuggestion) {
    const mid = assistantMessageIdRef.current;
    if (mid) {
      updateMessages((msgs) => msgs.map((m) => m.id === mid
        ? { ...m, content: m.content || suggestion.message, modelSuggestion: suggestion }
        : m));
    } else {
      const newId = Date.now().toString();
      assistantMessageIdRef.current = newId;
      updateMessages((msgs) => [...msgs, {
        id: newId, role: 'assistant' as const, content: suggestion.message,
        timestamp: new Date(), modelSuggestion: suggestion,
      }]);
    }
  }

  function handleAskUser(data: Record<string, unknown>) {
    const askId = typeof data.askId === 'string' ? data.askId : '';
    const questions = normalizeAskUserQuestions(data.questions);
    if (questions.length === 0) return;
    setPendingAskUser({ askId, questions });
    attachAskUser(questions);
  }

  function attachAskUser(questions: AskUserQuestion[]) {
    const mid = assistantMessageIdRef.current;
    if (mid) {
      updateMessages((msgs) => msgs.map((m) => m.id === mid ? { ...m, askUserQuestions: questions } : m));
    } else {
      const newId = Date.now().toString();
      assistantMessageIdRef.current = newId;
      updateMessages((msgs) => [...msgs, {
        id: newId, role: 'assistant' as const, content: '',
        timestamp: new Date(), askUserQuestions: questions,
      }]);
    }
  }

  // ── SSE stream hook ────────────────────────────────────────────────────
  const { readStream, abort: abortStream } = useSseStream(handleSseEvent);

  // ── Flush rAF buffer on tab visibility change ──────────────────────────
  // requestAnimationFrame is paused by browsers for hidden tabs.  If the user
  // switches away while text is streaming, the pending rAF callback never fires
  // and text stays in currentAssistantTextRef without reaching the query cache.
  // Flushing on hide: ensures cache is up-to-date before rAF stops firing.
  // Flushing on show: ensures content appears synchronously the moment the user
  // looks back, rather than waiting for the next rAF tick (~16 ms).  It also
  // guards against the edge case where cancelAnimationFrame ran in the cleanup
  // but the subsequent flushTextToCache() was skipped (e.g. page freeze).
  //
  // On mobile, the browser may silently close the TCP connection while the tab
  // is backgrounded (or after the app is sent to the background / screen locks).
  // When the user returns, reader.read() may stay pending indefinitely — or until
  // the 3-minute timeout — rather than rejecting with an error.  To recover
  // immediately, we cancel the current stream reader when the tab becomes visible
  // again after being hidden long enough for a mobile OS to have killed the TCP
  // connection (5 s threshold).  Cancelling the reader causes reader.read() to
  // resolve as { done: true } inside useSseStream, which returns false (no clean
  // `done` event), which throws SSE_STALE_CONNECTION, which triggers
  // attemptReconnect — recovering the stream within milliseconds.
  // We skip this for brief tab switches (< 5 s) so normal desktop usage (e.g.
  // alt-tab) doesn't cause spurious reconnects on still-healthy connections.
  const tabHiddenAtRef = useRef<number | null>(null);
  useEffect(() => {
    const onVisibilityChange = () => {
      if (rafFlushRef.current !== 0) {
        cancelAnimationFrame(rafFlushRef.current);
        flushTextToCache();
      }

      if (document.visibilityState === 'hidden') {
        tabHiddenAtRef.current = Date.now();
      } else {
        // On tab resume: if the tab was hidden long enough for mobile to have
        // closed the TCP connection, force-close the reader so the stale-
        // connection path fires immediately instead of waiting for the timeout.
        const hiddenMs = tabHiddenAtRef.current === null
          ? 0
          : Date.now() - tabHiddenAtRef.current;
        tabHiddenAtRef.current = null;

        const MOBILE_TCP_KILL_THRESHOLD_MS = 5_000;
        if (hiddenMs >= MOBILE_TCP_KILL_THRESHOLD_MS && activeStreamConvIdRef.current) {
          abortStream();
        }
      }
    };

    // When the browser regains network connectivity, attempt to reconnect
    // if a previous reconnect failed due to the network being unavailable.
    // Guards ensure we never tear down an active stream or duplicate an
    // in-flight reconnect.
    const onOnline = () => {
      if (activeStreamConvIdRef.current) return;   // live stream — don't disrupt
      if (reconnectAbortRef.current) return;        // reconnect already in flight
      if (abortControllerRef.current) return;       // send in flight — don't race
      if (!conversationIdRef.current) return;       // no conversation to reconnect to
      const state = queryClient.getQueryState<InfiniteData<MessagePage>>(
        conversationQueryKey(conversationIdRef.current),
      );
      if (hasCachedInsufficientCredits(state?.data)) return;
      // A page-0 fetch in flight would land on top of whatever a reconnect
      // attached now streams into the cache. Hand the attach back to the gated
      // mount effect, which runs again once that fetch settles (AIB-8372).
      if (!isReconnectSafe(state)) {
        mountReconnectDone.current = false;
        return;
      }
      attemptReconnect(conversationIdRef.current).catch(() => {});
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    globalThis.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      globalThis.removeEventListener('online', onOnline);
    };
  // flushTextToCache and abortStream only close over stable refs.
  // activeStreamConvIdRef, tabHiddenAtRef, reconnectAbortRef, and
  // conversationIdRef are refs so they're always current.
  // attemptReconnect is a stable useCallback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleGuardrail(data: Record<string, unknown>) {
    // Cancel any buffered text flush — the violating content must not land.
    if (rafFlushRef.current !== 0) {
      cancelAnimationFrame(rafFlushRef.current);
      rafFlushRef.current = 0;
    }
    currentAssistantTextRef.current = '';

    const mid = assistantMessageIdRef.current;
    const policy = (data.policy as string) || 'content_safety';
    if (!mid) {
      const newId = Date.now().toString();
      assistantMessageIdRef.current = newId;
      updateMessages((msgs) => [...msgs, {
        id: newId, role: 'assistant' as const, content: '', timestamp: new Date(),
        guardrailRetracted: true, guardrailPolicy: policy,
      }]);
      return;
    }
    const targetId = mid;
    updateMessages((msgs) => msgs.map((m) => m.id === targetId ? {
      ...m, content: '', guardrailRetracted: true, guardrailPolicy: policy,
      toolExecutions: undefined,
      inlineArtifacts: undefined, agentBoundaries: undefined,
    } : m));
  }

  function handleInterrupted(data: Record<string, unknown>) {
    if (rafFlushRef.current !== 0) {
      cancelAnimationFrame(rafFlushRef.current);
      flushTextToCache();
    }
    const realMessageId = data.messageId as string | undefined;
    const reason = typeof data.reason === 'string' ? data.reason : undefined;
    const mid = assistantMessageIdRef.current;
    const finalId = realMessageId || mid || `assistant-interrupted-${Date.now()}`;
    assistantMessageIdRef.current = finalId;
    updateMessages((msgs) => markAssistantInterrupted(msgs, mid, finalId, reason));
  }

  function handleReconnected(data: Record<string, unknown>) {
    // NOTE: Do NOT invalidateQueries here.  The persisted messages are already
    // loaded by the initial page query.  Invalidating would trigger a background
    // refetch that overwrites the cache — including the streaming message we're
    // about to add — causing subsequent SSE chunks to update a message that no
    // longer exists in the cache.  The post-stream invalidation in
    // attemptReconnect handles the final sync once the stream ends.
    const accText = (data.accumulatedText as string) || '';
    const persistedOffset = typeof data.persistedTextOffset === 'number' ? data.persistedTextOffset : 0;

    // Always look up the cache so we can derive the persisted prefix regardless
    // of whether msgId was already set (same-instance reconnect) or not.
    const cacheKey = conversationQueryKey(conversationIdRef.current);
    const cached = queryClient.getQueryData<InfiniteData<MessagePage>>(cacheKey);
    const msgs = cached?.pages[0]?.messages;
    const lastMsg = msgs && msgs.length > 0 ? msgs[msgs.length - 1] : undefined;

    // Reuse the existing trailing assistant message from the cache to avoid
    // creating duplicate messages on repeated conversation switches.
    let msgId = assistantMessageIdRef.current;
    if (!msgId) {
      if (lastMsg?.role === 'assistant') {
        msgId = lastMsg.id;
      } else {
        msgId = `assistant-reconnect-${Date.now()}`;
      }
    }
    assistantMessageIdRef.current = msgId;

    // Derive the persisted prefix from the cached message sliced to persistedOffset.
    // This handles both same-instance reconnects (where existingContent would have
    // been empty before) and cross-instance takeovers, ensuring currentAssistantTextRef
    // always reflects the full content (persisted prefix + new streaming text) so
    // subsequent position calculations (handleAgentContext, handleToolStart) are correct.
    const cachedContent =
      lastMsg?.role === 'assistant' && lastMsg.id === msgId ? lastMsg.content || '' : '';
    const baseContent = cachedContent.slice(0, persistedOffset);
    currentAssistantTextRef.current = baseContent + accText;

    const rawBoundaries = data.agentBoundaries as Array<Record<string, unknown>> | undefined;
    const reconnectBoundaries: AgentBoundary[] | undefined = rawBoundaries
      ?.filter((b) => typeof b.agent === 'string' && typeof b.startPos === 'number')
      .map((b) => ({
        agent: b.agent as string,
        displayName: agentDisplayName(b.agent as string),
        startPos: b.startPos as number,
      }));

    // Build a lookup from the backend's completedTools list so we can
    // transition stale "executing" chips to their correct final status.
    const rawCompleted = data.completedTools as RawCompletedTool[] | undefined;
    const completedMap = new Map<string, boolean>();
    if (rawCompleted) {
      for (const ct of rawCompleted) {
        completedMap.set(ct.toolUseId, ct.success);
      }
    }

    const rawExecuting = data.executingTools as RawExecutingTool[] | undefined;

    updateMessages((prev) => {
      const existing = prev.find((m) => m.id === msgId);
      if (existing) {
        // Use the persisted prefix (sliced to persistedOffset) as the base rather
        // than the full cached content so that streaming text from a previous
        // connection is not duplicated when accText arrives from the backend.
        const cachedExisting = existing.content || '';
        const normalizedBase = cachedExisting.slice(0, persistedOffset);
        const mergedContent = normalizedBase + accText;
        const existingBoundaries = existing.agentBoundaries ?? [];
        let mergedBoundaries: AgentBoundary[] | undefined;
        if (reconnectBoundaries?.length) {
          // The backend list is authoritative — dedupe against the cached list
          // so a same-instance reconnect doesn't double-append boundaries that
          // were already streamed into the cache before the reconnect event.
          const seen = new Map<string, AgentBoundary>();
          for (const b of [...existingBoundaries, ...reconnectBoundaries]) {
            seen.set(`${b.agent}-${b.startPos}`, b);
          }
          mergedBoundaries = [...seen.values()];
        } else if (existingBoundaries.length > 0) {
          mergedBoundaries = existingBoundaries;
        }
        const existingTools = reconciledTools(existing.toolExecutions, completedMap);
        const newTools = seedToolExecutions(rawCompleted, rawExecuting, 0);
        const mergedTools = mergeToolExecutions(existingTools, newTools);
        return prev.map((m) => m.id === msgId ? {
          ...m, content: mergedContent, isStreaming: true,
          agentBoundaries: mergedBoundaries?.length ? mergedBoundaries : undefined,
          toolExecutions: mergedTools,
        } : m);
      }
      const msg: Message = {
        id: msgId,
        role: 'assistant' as const,
        content: baseContent + accText,
        timestamp: new Date(),
        isStreaming: true,
        agentBoundaries: reconnectBoundaries?.length ? reconnectBoundaries : undefined,
        toolExecutions: seedToolExecutions(rawCompleted, rawExecuting, 0),
      };
      return [...prev, msg];
    });
    setIsReconnecting(false);
    setIsSending(true);
  }

  function handleToolStart(data: Record<string, unknown>) {
    const toolName = data.tool as string;
    const toolUseId = data.toolUseId as string | undefined;
    if (!toolName) return;

    if (toolName === LOAD_SKILL_TOOL && toolUseId) {
      ensureAssistantMessage();
      upsertSetupStep({
        id: toolUseId,
        kind: 'skill',
        status: 'active',
        skill: typeof data.summary === 'string' ? data.summary : undefined,
        insertPosition: toolInsertPosition(data, currentAssistantTextRef.current.length),
      });
      return;
    }

    if (INTERNAL_TOOLS.has(toolName)) return;

    if (rafFlushRef.current !== 0) {
      cancelAnimationFrame(rafFlushRef.current);
      rafFlushRef.current = 0;
    }
    const latestText = currentAssistantTextRef.current;
    const mid0 = assistantMessageIdRef.current;
    if (mid0) {
      updateMessages((msgs) => msgs.map((m) =>
        m.id === mid0 ? { ...m, content: latestText, isStreaming: true } : m,
      ));
    }

    if (toolUseId) {
      const cached = queryClient.getQueryData<InfiniteData<MessagePage>>(conversationQueryKey(conversationIdRef.current));
      if (isToolDuplicateInCache(toolUseId, assistantMessageIdRef.current, cached)) return;
    }

    // Capture args and summary for traceability (sent by backend)
    const argsJson = data.args as string | undefined;
    const summary = data.summary as string | undefined;

    const backendPos = typeof data.insertPosition === 'number' ? data.insertPosition : undefined;
    const agentNameFromSse = typeof data.agentName === 'string' ? data.agentName : undefined;
    const newExec: ToolExecution = {
      tool: toolName, toolUseId, status: 'executing',
      insertPosition: backendPos ?? currentAssistantTextRef.current.length,
      approvalArgs: argsJson,
      summary,
      agentName: agentNameFromSse,
    };

    const mid = assistantMessageIdRef.current;
    if (mid) {
      let transitioned = false;
      updateMessages((msgs) => msgs.map((m) => {
        if (m.id !== mid) return m;
        const { execs, transitioned: t } = applyPendingToExecuting(m.toolExecutions, toolName, toolUseId);
        if (t) transitioned = true;
        return { ...m, toolExecutions: execs };
      }));
      if (transitioned) return;

      let foundAndUpdated = false;
      updateMessages((msgs) => msgs.map((m) => {
        if (m.id !== mid || !m.toolExecutions) return m;
        const result = updateExecutingToolArgs(m.toolExecutions, toolUseId, argsJson, summary);
        if (result.found) foundAndUpdated = true;
        return { ...m, toolExecutions: result.updated };
      }));
      if (foundAndUpdated) return;

      updateMessages((msgs) => msgs.map((m) => m.id === mid ? appendToolExecution(m, newExec) : m));
    } else {
      const newId = Date.now().toString();
      assistantMessageIdRef.current = newId;
      updateMessages((msgs) => [...msgs, {
        id: newId, role: 'assistant' as const, content: currentAssistantTextRef.current || '',
        timestamp: new Date(), toolExecutions: [newExec],
      }]);
    }
  }

  function handleToolComplete(data: Record<string, unknown>) {
    const toolName = data.tool as string;
    const toolUseId = data.toolUseId as string | undefined;
    const success = data.success as boolean;
    if (!toolName) return;

    if (toolName === LOAD_SKILL_TOOL && toolUseId) {
      // The setup step is live status only: once the load settles it hands over
      // to a tool execution, which is what persists in the transcript.
      const step = setupStepsRef.current.find((s) => s.id === toolUseId);
      removeSetupStep(toolUseId);
      const skillMid = assistantMessageIdRef.current;
      if (!skillMid) return;
      const settled: ToolExecution = {
        tool: toolName,
        toolUseId,
        status: 'complete',
        success: success !== false,
        insertPosition: step?.insertPosition ?? currentAssistantTextRef.current.length,
        summary: step?.skill,
      };
      updateMessages((msgs) => msgs.map((m) => (
        m.id === skillMid ? completeSkillLoad(m, settled) : m
      )));
      return;
    }

    if (INTERNAL_TOOLS.has(toolName)) return;

    const artifactInfo = toolName === 'present_artifact' && data.result
      ? parseArtifactInfo(data.result as string) : undefined;
    const imageUrl = typeof data.imageUrl === 'string' ? data.imageUrl : undefined;

    const mid = assistantMessageIdRef.current;
    if (!mid) return;

    updateMessages((msgs) => msgs.map((m) =>
      m.id === mid ? markToolComplete(m, toolName, toolUseId, success, artifactInfo, imageUrl) : m,
    ));
  }

  function handleToolApprovalRequest(data: Record<string, unknown>) {
    const request = parseToolApprovalRequest(data);
    if (!request) return;
    const {
      tool: toolName,
      toolUseId,
      approvalArgs: argsJson,
      summary,
      agentName: agentNameFromSse,
    } = request;

    const mid = assistantMessageIdRef.current;
    if (mid) {
      let transitioned = false;
      updateMessages((msgs) => {
        if (hasPendingApprovalAnywhere(msgs, toolName, toolUseId)) return msgs;
        return msgs.map((m) => {
          if (m.id !== mid) return m;
          const result = applyExecutingToPendingApproval(
            m.toolExecutions, toolName, toolUseId, argsJson, summary,
          );
          if (result.transitioned) { transitioned = true; return { ...m, toolExecutions: result.execs }; }
          return m;
        });
      });
      if (transitioned) return;
    }

    const newExec: ToolExecution = {
      tool: toolName,
      toolUseId,
      status: 'pending_approval',
      insertPosition: currentAssistantTextRef.current.length,
      approvalArgs: argsJson,
      summary,
      agentName: agentNameFromSse,
    };

    if (mid) {
      updateMessages((msgs) => {
        if (hasPendingApprovalAnywhere(msgs, toolName, toolUseId)) return msgs;
        return msgs.map((m) => m.id === mid ? appendToolExecution(m, newExec) : m);
      });
    } else {
      const newId = Date.now().toString();
      updateMessages((msgs) => {
        if (hasPendingApprovalAnywhere(msgs, toolName, toolUseId)) return msgs;
        assistantMessageIdRef.current = newId;
        return [...msgs, {
          id: newId, role: 'assistant' as const, content: currentAssistantTextRef.current || '',
          timestamp: new Date(), toolExecutions: [newExec],
        }];
      });
    }
  }

  function handleToolRejected(data: Record<string, unknown>) {
    const toolName = data.tool as string;
    const toolUseId = data.toolUseId as string | undefined;
    if (!toolName) return;

    const mid = assistantMessageIdRef.current;
    if (!mid) return;

    updateMessages((msgs) => msgs.map((m) => {
      if (m.id !== mid) return m;
      return { ...m, toolExecutions: applyToolRejected(m.toolExecutions, toolName, toolUseId) };
    }));
  }

  /** Flush accumulated assistant text to the React Query cache (at most once per rAF). */
  function flushTextToCache() {
    rafFlushRef.current = 0;
    const mid = assistantMessageIdRef.current;
    const text = currentAssistantTextRef.current;
    if (mid) {
      updateMessages((msgs) => msgs.map((m) =>
        m.id === mid ? { ...m, content: text, isStreaming: true } : m,
      ));
    } else {
      const newId = Date.now().toString();
      assistantMessageIdRef.current = newId;
      updateMessages((msgs) => [...msgs, {
        id: newId, role: 'assistant' as const, content: text,
        timestamp: new Date(), isStreaming: true,
      }]);
    }
  }

  function appendTextChunk(text: string) {
    currentAssistantTextRef.current += text;
    // Throttle React state updates to one per animation frame. During fast LLM
    // streaming, chunks arrive faster than 60fps — batching them prevents dozens
    // of redundant setQueryData calls per second that cause setTimeout violations.
    if (rafFlushRef.current === 0) {
      rafFlushRef.current = requestAnimationFrame(() => flushTextToCache());
    }
    // Onboarding funnel: fire os_first_output on the first non-empty assistant chunk,
    // not after readStream() completes, so cancels/errors don't suppress the event.
    // Key is namespaced by user ID to avoid cross-login contamination.
    if (text) {
      const uid = userIdRef.current;
      const firstOutputKey = uid ? `os_funnel_first_output_sent:${uid}` : 'os_funnel_first_output_sent';
      if (!localStorage.getItem(firstOutputKey)) {
        localStorage.setItem(firstOutputKey, '1');
        trackFunnelEvent('os_first_output');
      }
    }
  }

  function handleInsufficientCredits(data: Record<string, unknown>) {
    const canManageBilling = Boolean(data.canManageBilling);
    // Preserve null/undefined: only treat as active if explicitly true.
    // Boolean(null) === false would incorrectly treat "unknown" as free-tier.
    let hasActiveSubscription: boolean | null = null;
    if (data.hasActiveSubscription === true) hasActiveSubscription = true;
    else if (data.hasActiveSubscription === false) hasActiveSubscription = false;
    const content = getInsufficientCreditsMessage(canManageBilling, hasActiveSubscription);

    const newId = `credits-${Date.now()}`;
    updateMessages((msgs) => [...msgs, {
      id: newId,
      role: 'assistant' as const,
      content,
      timestamp: new Date(),
      insufficientCredits: { canManageBilling, hasActiveSubscription },
    }]);
    setIsSending(false);
    dismissSetup();
  }

  function ensureAssistantMessage(): string {
    let mid = assistantMessageIdRef.current;
    if (mid) return mid;

    if (rafFlushRef.current !== 0) {
      cancelAnimationFrame(rafFlushRef.current);
      rafFlushRef.current = 0;
    }
    const text = currentAssistantTextRef.current;
    mid = Date.now().toString();
    assistantMessageIdRef.current = mid;
    updateMessages((msgs) => [...msgs, {
      id: mid!,
      role: 'assistant' as const,
      content: text,
      timestamp: new Date(),
      isStreaming: true,
    }]);
    return mid;
  }

  function handleAgentContext(data: Record<string, unknown>) {
    const agentNameRaw = data.agentName as string | undefined;
    const event = data.event as string | undefined;
    if (!agentNameRaw || !event) return;

    // Flush pending text so content and boundary positions are always consistent
    // in the same query cache update (avoids RAF lag causing stale content).
    if (rafFlushRef.current !== 0) {
      cancelAnimationFrame(rafFlushRef.current);
      rafFlushRef.current = 0;
    }
    const latestText = currentAssistantTextRef.current;
    const rawStartPos = data.startPos;
    let backendStartPos: number | undefined;
    if (typeof rawStartPos === 'number') {
      backendStartPos = rawStartPos;
    } else if (typeof rawStartPos === 'string' && rawStartPos !== '') {
      backendStartPos = Number(rawStartPos);
    }
    const startPos = (backendStartPos != null && !Number.isNaN(backendStartPos))
      ? backendStartPos
      : latestText.length;

    const mid = ensureAssistantMessage();
    updateMessages((msgs) =>
      msgs.map((m) => {
        if (m.id !== mid) return m;
        const existing = m.agentBoundaries ?? [];
        const newBoundaries = [...existing];

        if (event === 'start') {
          // If the same agent was just closed (pattern: [..., agentX, os]),
          // remove the trailing "os" boundary to extend the existing agent block
          // instead of creating a new one. This merges consecutive same-agent
          // invocations into one continuous group during streaming.
          if (shouldMergeConsecutiveAgent(newBoundaries, agentNameRaw, latestText, startPos, m.toolExecutions, m.inlineArtifacts)) {
            newBoundaries.pop();
            return { ...m, content: latestText, isStreaming: true, agentBoundaries: newBoundaries };
          }

          if (newBoundaries.length === 0 && startPos > 0) {
            newBoundaries.push({ agent: 'os', displayName: agentDisplayName('os'), startPos: 0 });
          }
          newBoundaries.push({
            agent: agentNameRaw,
            displayName: agentDisplayName(agentNameRaw),
            startPos,
          });
        } else if (event === 'end') {
          newBoundaries.push({
            agent: 'os',
            displayName: agentDisplayName('os'),
            startPos,
          });
        }

        return { ...m, content: latestText, isStreaming: true, agentBoundaries: newBoundaries };
      }),
    );
  }

  function handleInlineArtifact(data: Record<string, unknown>) {
    const artifact = normalizeArtifact(data);
    if (!artifact) {
      console.warn('Ignoring inline_artifact event with missing fields:', data);
      return;
    }

    artifact.insertPosition = currentAssistantTextRef.current.length;

    const mid = ensureAssistantMessage();
    updateMessages((msgs) => msgs.map((m) => appendArtifactToMessage(m, mid, artifact)));
  }

  function handleDefaultEvent(data: Record<string, unknown>) {
    if (data.text) appendTextChunk(data.text as string);

    // Legacy tool events (untyped — tool field without event type)
    if (data.tool && data.success === undefined) handleToolStart(data);
    else if (data.tool && data.success !== undefined) handleToolComplete(data);

    if (data.error) {
      setSendError((data.message as string) || (data.error as string));
      // The banner is ephemeral React state, so a reload would leave a user
      // message with no reply. Java persists the same flag this sets.
      handleInterrupted(data);
    }
  }

  // ── Reset state when conversationId changes ────────────────────────────
  const prevConversationIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    // If there's a live SSE stream for this exact conversation, don't kill it.
    // This happens during the null→realId URL transition for new conversations.
    // Only skip reset when there is an active, non-null stream for the same conversation.
    if (activeStreamConvIdRef.current != null && activeStreamConvIdRef.current === conversationId) {
      prevConversationIdRef.current = conversationId;
      return;
    }

    // Skip reset when the conversationId prop hasn't actually changed (e.g.
    // React StrictMode remount, or re-renders triggered by unrelated state
    // updates like setIsSending).  We intentionally do NOT also check
    // conversationIdRef.current here: setupUploadedConversation() legitimately
    // sets the ref to a new id while the prop is still null, and a re-render
    // before the URL catches up must not clobber that id.
    if (prevConversationIdRef.current === conversationId) {
      return;
    }
    prevConversationIdRef.current = conversationId;

    // Genuine conversation switch — update ref and reset everything
    conversationEpochRef.current += 1;
    conversationIdRef.current = conversationId;
    currentAssistantTextRef.current = '';
    assistantMessageIdRef.current = null;
    requestIdRef.current = null;
    activeStreamConvIdRef.current = null;
    if (rafFlushRef.current !== 0) {
      cancelAnimationFrame(rafFlushRef.current);
      rafFlushRef.current = 0;
    }
    setIsSending(false);
    setSendError(null);
    resetSetup();
    setPendingModelSuggestion(null);
    hasRestoredModelSuggestion.current = false;
    setPendingAskUser(null);
    hasRestoredAskUser.current = false;
    setIsReconnecting(false);
    failedUploadMsgIdRef.current = null;
    lastSendOptionsRef.current = {};

    // Refresh the sidebar so any server-created conversations (e.g. a new
    // chat whose SSE was aborted before resolveConversationId ran) appear.
    fetchConversations();

    if (reconnectAbortRef.current) {
      reconnectAbortRef.current.abort();
      reconnectAbortRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // AIB-6610: cancel any pending post-stream invalidate from the previous
    // conversation so it doesn't fire against the newly-selected one.
    if (streamSyncTimeoutRef.current) {
      clearTimeout(streamSyncTimeoutRef.current);
      streamSyncTimeoutRef.current = null;
    }
    abortStream();
  }, [conversationId, abortStream, fetchConversations, resetSetup]);

  // ── Infinite query for persisted messages ──────────────────────────────
  const {
    data: infiniteData,
    isLoading: isLoadingMessages,
    fetchStatus: messagesFetchStatus,
    error: messagesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<MessagePage>({
    queryKey: conversationQueryKey(conversationId),
    queryFn: ({ pageParam }) => fetchMessagePage(conversationId!, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.hasMore ? last.oldestTimestamp ?? undefined : undefined,
    enabled: !!conversationId && !!user,
    staleTime: 5 * 60 * 1000,
    retry: (count, error) => {
      if (error && 'response' in error) {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status === 404) { navigate('/', { replace: true }); return false; }
        if (status === 401 || status === 403) return false;
      }
      return count < 2;
    },
  });

  const messages: Message[] = infiniteData
    ? mergeConsecutiveAssistantMessages(
        [...infiniteData.pages].reverse().flatMap((p) => p.messages),
      )
    : [];

  // AIB-8372: consumers gate the composer on this, so a send cannot start before
  // there is a transcript to send into. Latched per conversation: page 0 refetches
  // again after every turn and after a reconnect, and reading those as unsettled
  // would pull the chat out from under the user each time, draft and all. A failed
  // load counts as settled so the consumer shows the error, not a spinner.
  const historyLanded = (infiniteData?.pages.length ?? 0) > 0 && messagesFetchStatus === 'idle';
  const historySettledFor = useRef<string | null>(null);
  useEffect(() => {
    if (historyLanded) historySettledFor.current = conversationId;
  }, [historyLanded, conversationId]);
  const isHistorySettled = !conversationId
    || historyLanded
    || !!messagesError
    || historySettledFor.current === conversationId;

  // Restore a pending model-swap suggestion (AIB-7877): still open only when the
  // LAST message is the assistant turn that raised it — any later message means
  // the user already accepted or dismissed it.
  const hasRestoredModelSuggestion = useRef(false);
  useEffect(() => {
    if (hasRestoredModelSuggestion.current || !infiniteData || messages.length === 0) return;
    const last = messages.at(-1);
    if (last?.role === 'assistant' && last.modelSuggestion) {
      setPendingModelSuggestion(last.modelSuggestion);
    }
    hasRestoredModelSuggestion.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infiniteData]);

  // Restore a pending clarification batch (AIB-6745): the questions are still
  // open only when the LAST message is the assistant turn that asked them — any
  // later message means the user already answered or skipped.
  const hasRestoredAskUser = useRef(false);
  useEffect(() => {
    if (hasRestoredAskUser.current || !infiniteData || messages.length === 0) return;
    const last = messages.at(-1);
    if (last?.role === 'assistant' && last.askUserQuestions?.length) {
      setPendingAskUser({ askId: last.id, questions: last.askUserQuestions });
    }
    hasRestoredAskUser.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infiniteData]);

  // ── Send mutation ──────────────────────────────────────────────────────
  const sendMessageMutation = useMutation({
    mutationFn: async ({ message, conversationId: convId, attachmentIds, mode, runnerType, approvalMode, clarification, viewSnapshot, signal }: {
      message: string; conversationId: string | null; attachmentIds?: string[];
      optimisticAttachments?: MessageAttachment[]; mode?: string;
      runnerType?: string; approvalMode?: string;
      clarification?: ClarificationSubmission;
      viewSnapshot?: ViewSnapshot | null;
      signal: AbortSignal;
    }) => {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required. Please log in.');

      const body: Record<string, unknown> = {
        message,
        conversationId: convId,
        runnerType: runnerType || 'cloud', // ALWAYS send runnerType (default to 'cloud')
        approvalMode: approvalMode || 'require_approval', // ALWAYS send approvalMode
      };
      if (attachmentIds?.length) body.attachmentIds = attachmentIds;
      if (mode) body.mode = mode;
      if (clarification?.questionAnswers?.length) body.questionAnswers = clarification.questionAnswers;
      if (clarification?.questionsSkipped) body.questionsSkipped = true;
      if (viewSnapshot) body.viewSnapshot = viewSnapshot;

      const llmModel = getStoredLlmModel(convId);
      if (llmModel !== LLM_MODEL_AUTO) body.llmModel = llmModel;

      const dismissedModelSuggestions = getDismissedModelSuggestions(convId);
      if (dismissedModelSuggestions.length) body.dismissedModelSuggestions = dismissedModelSuggestions;

      const doFetch = (t: string) => fetch(`${API_BASE_URL}/api/v1/conversations/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
        body: JSON.stringify(body),
        signal,
      });

      const response = await fetchWithTokenRefresh(doFetch, token);

      if (!response.ok) {
        if (response.status === 429) {
          const errBody = await response.json().catch(() => null);
          throw new Error(
            errBody?.message ?? 'Too many active conversations. Please wait or cancel one.',
          );
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return { response, userMessage: message };
    },
    onMutate: async ({ message, optimisticAttachments, conversationId: convId, clarification }) => {
      const userMsg: Message = {
        id: Date.now().toString(), role: 'user', content: message, timestamp: new Date(),
        attachments: optimisticAttachments,
        askUserAnswers: clarification?.questionsSkipped
          ? undefined
          : (clarification?.questionAnswers as AskUserAnswer[] | undefined),
      };
      const key = conversationQueryKey(convId);
      // Written before waiting on anything below, so the message and the thinking
      // indicator still appear in the same render.
      let previousData = queryClient.getQueryData<InfiniteData<MessagePage>>(key);
      queryClient.setQueryData<InfiniteData<MessagePage>>(key, (old) => withOptimisticUserMessage(old, userMsg));

      await settlePendingHistory(queryClient, key);
      // A fetch that landed while we waited replaced page 0 from a snapshot taken
      // before the write above, so the message has to go back on top of it.
      const landed = queryClient.getQueryData<InfiniteData<MessagePage>>(key);
      if (!landed?.pages[0]?.messages.some((m) => m.id === userMsg.id)) {
        previousData = landed;
        queryClient.setQueryData<InfiniteData<MessagePage>>(key, (old) => withOptimisticUserMessage(old, userMsg));
      }

      await queryClient.cancelQueries({ queryKey: key });
      return { previousData };
    },
    onError: (error, variables, context) => {
      if (variables?.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) return;
      const key = conversationQueryKey(variables?.conversationId);
      if (context?.previousData) queryClient.setQueryData(key, context.previousData);
      setSendError(error instanceof Error ? error.message : 'Failed to send message');
      setIsSending(false);
    },
  });

  // ── Stream error handler ───────────────────────────────────────────────
  async function handleStreamError(error: unknown) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      setIsSending(false);
      return;
    }
    // When a conversation has been established, all stream errors are
    // potentially recoverable: the backend orchestration continues
    // regardless of client disconnects, so /reconnect can re-attach to an
    // active stream or load the final state from the database.  This
    // covers mobile browsers killing TCP connections (TypeError/network
    // errors) as well as stale connections and server shutdowns.
    if (conversationIdRef.current) {
      console.debug('[useChat] Stream error, attempting reconnect:', error);
      await attemptReconnect(conversationIdRef.current);
      return;
    }
    // No conversation established yet — can't reconnect.
    console.error('Chat error:', error);
    setSendError(error instanceof Error ? error.message : 'Failed to send message');
    setIsSending(false);
  }

  // ── sendMessage ────────────────────────────────────────────────────────
  async function resolveAttachments(files?: File[]) {
    if (!files || files.length === 0) return { attachmentIds: undefined, optimisticAttachments: undefined, conversationId: undefined as string | undefined };
    const uploaded = await uploadFiles(files, conversationIdRef.current);
    return { attachmentIds: uploaded.attachmentIds, optimisticAttachments: uploaded.optimisticAttachments, conversationId: uploaded.conversationId as string | undefined };
  }

  function stopStreamingForMessage(mid: string | null) {
    if (mid) {
      updateMessages((msgs) => msgs.map((m) => m.id === mid ? { ...m, isStreaming: false } : m));
    }
    // AIB-6610: if a tool_complete frame was dropped (e.g. tab hidden mid-turn),
    // the chip stays stuck as "executing" forever.  Debounce a refetch so the
    // in-flight rAF text flush finishes first; collectToolExecutions then
    // unconditionally promotes every DB tool_result to status=complete.
    // Pattern mirrors cancelRequest (which already invalidates after 1500ms).
    //
    // The timer is owned by `streamSyncTimeoutRef` so a subsequent send /
    // reconnect / conversation-switch / unmount can cancel it — otherwise a
    // late refetch would clobber the live stream's in-memory state that
    // handleReconnected or sendMessage just wrote.
    const convId = conversationIdRef.current;
    if (!convId) return;
    const key = conversationQueryKey(convId);
    if (streamSyncTimeoutRef.current) {
      clearTimeout(streamSyncTimeoutRef.current);
      streamSyncTimeoutRef.current = null;
    }
    streamSyncTimeoutRef.current = setTimeout(() => {
      streamSyncTimeoutRef.current = null;
      // If a new stream / reconnect started in the interim, skip: refetching
      // now would blow away the freshly-written live state.
      if (activeStreamConvIdRef.current || abortControllerRef.current || reconnectAbortRef.current) {
        return;
      }
      console.debug('[useChat] stream complete, invalidating', convId);
      queryClient.invalidateQueries({ queryKey: key }).catch(() => {});
    }, 500);
  }

  function setupUploadedConversation(uploadConvId: string, prevConvId: string | null, message: string) {
    conversationIdRef.current = uploadConvId;
    if (prevConvId) return;
    const cur = queryClient.getQueryData<InfiniteData<MessagePage>>(conversationQueryKey(null));
    if (cur) {
      queryClient.setQueryData(conversationQueryKey(uploadConvId), cur);
      queryClient.removeQueries({ queryKey: conversationQueryKey(null) });
    }
    const titleSuffix = message.length > 50 ? '...' : '';
    prependConversation({
      conversationId: uploadConvId,
      title: message.substring(0, 50) + titleSuffix,
      createdAt: new Date().toISOString(),
      lastActivityAt: new Date().toISOString(),
      messageCount: 1,
    });
    activeStreamConvIdRef.current = uploadConvId;
    setResolvedConversationId(uploadConvId);
  }

  const trackOnboardingFunnelEvents = (uid: string | undefined) => {
    const ns = (base: string) => uid ? `${base}:${uid}` : base;
    const firstPromptKey = ns('os_funnel_first_prompt_sent');
    if (!localStorage.getItem(firstPromptKey)) {
      localStorage.setItem(firstPromptKey, '1');
      trackFunnelEvent('os_first_prompt');
    }
    const secondSessionSentKey = ns('os_funnel_second_session_sent');
    const sessionCountedKey = ns('os_funnel_session_counted');
    const sessionCountKey = ns('os_funnel_session_count');
    if (!sessionStorage.getItem(sessionCountedKey)) {
      sessionStorage.setItem(sessionCountedKey, '1');
      const sessionCount = Number(localStorage.getItem(sessionCountKey) ?? 0) + 1;
      localStorage.setItem(sessionCountKey, String(sessionCount));
      if (sessionCount >= 2 && !localStorage.getItem(secondSessionSentKey)) {
        localStorage.setItem(secondSessionSentKey, '1');
        trackFunnelEvent('os_second_session');
      }
    }
  };

  function flushPendingText() {
    if (rafFlushRef.current !== 0) {
      cancelAnimationFrame(rafFlushRef.current);
      flushTextToCache();
    }
  }

  function isSendSuperseded(controller: AbortController, epoch: number) {
    return controller.signal.aborted || epoch !== conversationEpochRef.current;
  }

  async function settleFailedSend(error: unknown, epoch: number, targetConvId: string | null) {
    if (epoch !== conversationEpochRef.current) {
      queryClient.invalidateQueries({ queryKey: conversationQueryKey(targetConvId), refetchType: 'none' }).catch(() => {});
      return;
    }
    activeStreamConvIdRef.current = null;
    flushPendingText();
    stopStreamingForMessage(assistantMessageIdRef.current);
    await handleStreamError(error);
  }

  async function prepareAttachmentsForSend(files: File[] | undefined, message: string, epoch: number) {
    try {
      const result = await resolveAttachments(files);
      return {
        attachmentIds: result.attachmentIds,
        optimisticAttachments: result.optimisticAttachments,
        uploadConvId: result.conversationId,
      };
    } catch (uploadError) {
      if (epoch !== conversationEpochRef.current) return null;
      const failedMsgId = Date.now().toString();
      failedUploadMsgIdRef.current = failedMsgId;
      updateMessages((msgs) => [...msgs, {
        id: failedMsgId, role: 'user' as const, content: message, timestamp: new Date(),
      }]);
      setSendError(uploadError instanceof Error ? uploadError.message : 'Failed to upload file');
      return null;
    }
  }

  const sendMessage = useCallback(async (message: string, files?: File[], mode?: string, runnerType?: string, approvalMode?: string, options?: { clarification?: ClarificationSubmission; withoutSnapshot?: boolean }) => {
    const { clarification, withoutSnapshot = false } = options ?? {};
    const hasAnswers = (clarification?.questionAnswers?.length ?? 0) > 0;
    if (!message.trim() && (!files || files.length === 0) && !hasAnswers) return false;
    // Taken now, while the screen still shows what the user is talking about,
    // and awaited alongside the uploads; it settles within its own timeout.
    const viewSnapshot = withoutSnapshot ? Promise.resolve(null) : (snapshotRef.current?.() ?? Promise.resolve(null));
    // A new send always retires any open clarification / suggestion card,
    // and any setup block still fading out from the previous turn.
    setPendingAskUser(null);
    setPendingModelSuggestion(null);
    resetSetup();

    lastSendOptionsRef.current = { mode, runnerType, approvalMode };

    // Onboarding funnel: storage keys are namespaced by user ID so state is never
    // shared across logins on the same browser. Read from the ref so we always
    // get the current user ID even when sendMessage is a stale closure.
    trackOnboardingFunnelEvents(userIdRef.current);

    // Abort any in-progress reconnect before starting a send.  Without this,
    // readStream() (called below) cancels the reconnect's stream reader via its
    // internal abort(), causing attemptReconnect to fall through to
    // invalidateQueries + setIsSending(false) — which erases the optimistic
    // user message and removes the Thinking... indicator mid-send.
    if (reconnectAbortRef.current) {
      reconnectAbortRef.current.abort();
      reconnectAbortRef.current = null;
    }
    // AIB-6610: cancel any pending post-stream invalidate so it can't refetch
    // on top of the optimistic state this send is about to write.
    if (streamSyncTimeoutRef.current) {
      clearTimeout(streamSyncTimeoutRef.current);
      streamSyncTimeoutRef.current = null;
    }

    // When the user sends a new message the previous turn is cancelled server-side,
    // which clears any pending approval from Redis.  The `tool_rejected` SSE event
    // is sent on the old (now-closed) SSE stream and never reaches the frontend, so
    // any `pending_approval` chip stays stuck in that state.  Clear them proactively
    // so the UI is consistent and the stale Approve button can't trigger a 404.
    updateMessages((msgs) => msgs.map(clearPendingApprovals));

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const epoch = conversationEpochRef.current;
    requestIdRef.current = null;
    userMessageRef.current = message;
    currentAssistantTextRef.current = '';
    assistantMessageIdRef.current = null;

    if (failedUploadMsgIdRef.current) {
      const idToRemove = failedUploadMsgIdRef.current;
      failedUploadMsgIdRef.current = null;
      updateMessages((msgs) => msgs.filter((m) => m.id !== idToRemove));
    }
    setSendError(null);
    setCompacting(false);

    // True once the POST has landed and the turn is in flight. Distinguishes a
    // mid-stream failure (turn already dispatched — don't restore the caller's
    // card) from mutateAsync rejecting before the request left (nothing sent).
    let dispatched = false;
    const prevConvId = conversationIdRef.current;
    let targetConvId = prevConvId;
    try {
      const prepared = await prepareAttachmentsForSend(files, message, epoch);
      // Upload failed: nothing was sent. Signal it so callers holding a card
      // (e.g. an ask_user region answer) can restore it for a retry.
      if (!prepared) return false;
      const { attachmentIds, optimisticAttachments, uploadConvId } = prepared;
      const takenSnapshot = await viewSnapshot;
      if (isSendSuperseded(controller, epoch)) return false;

      // Set isSending immediately before the mutation so the optimistic user
      // message (added in onMutate) and the thinking indicator appear in the
      // same render — avoiding a glitch frame where Thinking... shows with no
      // user message visible yet.
      setIsSending(true);
      // A conversation the upload created is only put on the ref here, once the
      // send is known not to be stale.
      targetConvId = uploadConvId ?? conversationIdRef.current;
      if (uploadConvId) {
        setupUploadedConversation(uploadConvId, prevConvId, message);
      }

      const result = await sendMessageMutation.mutateAsync({
        message, conversationId: targetConvId,
        attachmentIds, optimisticAttachments, mode, runnerType, approvalMode,
        clarification, viewSnapshot: takenSnapshot, signal: controller.signal,
      });
      dispatched = true;
      if (epoch !== conversationEpochRef.current) {
        result.response.body?.cancel().catch(() => {});
        return true;
      }

      const reader = result.response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const clean = await readStream(reader);
      if (!clean) throw new Error('SSE_STALE_CONNECTION');

      // Flush any buffered text that hadn't landed yet before the stream closed.
      flushPendingText();
      stopStreamingForMessage(assistantMessageIdRef.current);
      activeStreamConvIdRef.current = null;
      dismissSetup();
      setIsSending(false);
      return true;
    } catch (error) {
      await settleFailedSend(error, epoch, targetConvId);
      // If the POST landed, the turn is in flight and a mid-stream failure must
      // NOT restore the caller's card. If mutateAsync rejected before dispatch
      // (4xx, auth, dropped connection), nothing was sent — report that so the
      // caller can restore the card and the user's answers aren't lost.
      return dispatched;
    } finally {
      if (abortControllerRef.current === controller) abortControllerRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readStream]);

  // ── Reconnect logic ────────────────────────────────────────────────────

  /** Flush any pending rAF buffer, clear the active stream ref, and refetch from DB. */
  async function finaliseReconnect(convId: string) {
    flushPendingText();
    activeStreamConvIdRef.current = null;
    const key = conversationQueryKey(convId);
    await queryClient.invalidateQueries({ queryKey: key });
    setIsReconnecting(false);
    setIsSending(false);
    dismissSetup();
  }

  /** Handle fatal reconnect failure: log, invalidate cache, and reset UI state. */
  async function handleReconnectFailure(err: unknown, convId: string) {
    // A network blip is already recovered from here; console.error would promote
    // it to a Datadog Error Tracking issue (AIB-8475).
    if (isNetworkError(err)) console.warn('[useChat] Reconnect failed (network):', err);
    else console.error('[useChat] Reconnect failed:', err);
    activeStreamConvIdRef.current = null;
    const key = conversationQueryKey(convId);
    await queryClient.invalidateQueries({ queryKey: key }).catch(() => {});
    setIsReconnecting(false);
    setIsSending(false);
    setCompacting(false);
    resetSetup();
  }

  const attemptReconnect = useCallback(async (
    convId: string | null,
    _retryCount = 0,
    _deadline: number | null = null,
  ) => {
    if (!convId) { setIsReconnecting(false); setIsSending(false); return; }

    const token = getAuthToken();
    if (!token) {
      setSendError('Authentication expired — please refresh the page');
      setIsReconnecting(false);
      setIsSending(false);
      return;
    }

    // AIB-6610: cancel any pending post-stream invalidate from a previous turn
    // so it can't fire mid-reconnect and erase the catch-up state we're about
    // to apply via handleReconnected.
    if (streamSyncTimeoutRef.current) {
      clearTimeout(streamSyncTimeoutRef.current);
      streamSyncTimeoutRef.current = null;
    }

    // Register an AbortController so sendMessage can cancel us before starting
    // its own stream.  This prevents our post-stream side-effects (invalidateQueries,
    // setIsSending(false)) from racing with the in-flight send.
    const abort = new AbortController();
    reconnectAbortRef.current = abort;

    try {
      const doReconnect = (t: string) => fetch(
        `${API_BASE_URL}/api/v1/conversations/${convId}/reconnect`,
        { headers: { Authorization: `Bearer ${t}` }, signal: abort.signal },
      );

      const response = await fetchWithTokenRefresh(doReconnect, token);

      if (response.status === 401 || response.status === 403) {
        setSendError('Authentication expired — please refresh the page');
        setIsReconnecting(false);
        setIsSending(false);
        resetSetup();
        return;
      }
      if (!response.ok || !response.body) throw new Error(`Reconnect failed: ${response.status}`);

      const clean = await readStream(response.body.getReader());
      if (!clean) throw new Error('SSE_STALE_CONNECTION');

      if (!abort.signal.aborted) await finaliseReconnect(convId);
    } catch (err) {
      if (isAbortedOrCancelled(err, abort.signal)) return;

      const budget = reconnectBudget(err, _retryCount, _deadline);
      if (!isRetryableReconnectError(err) || budget.exhausted) {
        await handleReconnectFailure(err, convId);
        return;
      }
      await new Promise((r) => setTimeout(r, reconnectRetryDelayMs(err.message, _retryCount)));
      if (!abort.signal.aborted) return attemptReconnect(convId, _retryCount + 1, budget.deadline);
    } finally {
      if (reconnectAbortRef.current === abort) {
        reconnectAbortRef.current = null;
      }
    }
  // flushTextToCache is a hoisted function that only closes over stable refs and
  // updateMessages (a stable useCallback).  Adding it as a dep would cause the
  // callback to be recreated every render — safe to omit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, readStream]);

  // ── Mount-only reconnect (NO isSending dependency!) ────────────────────
  const mountReconnectDone = useRef(false);

  useEffect(() => {
    // Reset on conversation change
    mountReconnectDone.current = false;
    setCompacting(false);
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId || mountReconnectDone.current) return;

    // React Query subscribes before this effect runs, so a refetch it just started
    // is not in this render's snapshot yet — the live state is the only honest read.
    const key = conversationQueryKey(conversationId);
    if (!isReconnectSafe(queryClient.getQueryState<InfiniteData<MessagePage>>(key))) return;

    // Something else already owns the stream, so there is nothing to attach to.
    // A send counts: waiting for page 0 means the user can get a turn away
    // before this runs, and attaching to it would clobber what it is writing.
    if (activeStreamConvIdRef.current || reconnectAbortRef.current || abortControllerRef.current) {
      mountReconnectDone.current = true;
      return;
    }

    // Skip reconnect if the cache already contains client-only state (e.g. the
    // insufficient-credits purchase UI) that would be wiped when finaliseReconnect
    // calls invalidateQueries and React Query refetches from the database.
    if (hasCachedInsufficientCredits(queryClient.getQueryData<InfiniteData<MessagePage>>(key))) {
      mountReconnectDone.current = true;
      return;
    }

    setIsReconnecting(true);
    mountReconnectDone.current = true;

    let started = false;
    const timeoutId = setTimeout(() => {
      started = true;
      // A send made in the meantime, such as a first message sent on mount, owns the stream now.
      if (abortControllerRef.current || activeStreamConvIdRef.current) {
        setIsReconnecting(false);
        return;
      }
      attemptReconnect(conversationId).catch((err) => {
        console.warn('[useChat] Mount reconnect failed:', err);
      });
    }, 0);

    // A history refetch before the attempt starts cancels it; it must be scheduled again, not left "Resuming".
    return () => {
      if (started) return;
      clearTimeout(timeoutId);
      mountReconnectDone.current = false;
      setIsReconnecting(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, infiniteData, messagesFetchStatus]);

  // ── Cancel ─────────────────────────────────────────────────────────────
  const cancelRequest = useCallback(() => {
    const mid = assistantMessageIdRef.current;
    // Flush any buffered text and finalize the current streaming message in the
    // React Query cache BEFORE aborting the stream.  This preserves what the
    // user already saw — the abort causes sendMessage's catch block to run
    // later, but by then the RAF is already cancelled so it won't double-flush.
    // Capture the streamed text only if the RAF hasn't already flushed it.
    let finalText: string | undefined;
    if (rafFlushRef.current !== 0) {
      cancelAnimationFrame(rafFlushRef.current);
      rafFlushRef.current = 0;
      finalText = currentAssistantTextRef.current;
    }
    // The control plane records the stop the same way, so this survives the refetch below.
    const stoppedId = mid ?? `assistant-interrupted-${Date.now()}`;
    updateMessages((msgs) => markAssistantInterrupted(
      finalText === undefined ? msgs : msgs.map((m) => (m.id === mid ? { ...m, content: finalText } : m)),
      mid, stoppedId, 'cancelled',
    ));

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    abortStream();

    const convId = conversationIdRef.current;
    const token = convId ? getAuthToken() : null;
    if (convId && token) {
      const key = conversationQueryKey(convId);
      // A send started meanwhile owns the cache; refetching would drop its live message.
      const resync = () => {
        if (activeStreamConvIdRef.current || abortControllerRef.current || reconnectAbortRef.current) return;
        queryClient.invalidateQueries({ queryKey: key }).catch(() => {});
      };
      const turnId = requestIdRef.current;
      const body = turnId ? JSON.stringify({ requestId: turnId }) : undefined;
      // Returns once this instance has saved the stopped turn (bounded server-side). A turn
      // owned by another instance is saved shortly after, hence the late resync. Resyncing
      // earlier would replace the streamed text with a DB that does not have it yet.
      fetch(`${API_BASE_URL}/api/v1/conversations/${convId}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
        body,
      }).catch(() => {}).finally(resync);
      // Its own timer: the aborted send's cleanup replaces streamSyncTimeoutRef.
      if (stoppedTurnResyncRef.current) clearTimeout(stoppedTurnResyncRef.current);
      stoppedTurnResyncRef.current = setTimeout(() => {
        stoppedTurnResyncRef.current = null;
        resync();
      }, STOPPED_TURN_LATE_RESYNC_MS);
    }

    requestIdRef.current = null;
    setIsSending(false);
    setCompacting(false);
    dismissSetup();
  }, [abortStream, queryClient, updateMessages, dismissSetup]);

  /**
   * Sends `prompt`, or else the user message that produced `interruptedMessageId`,
   * as a fresh turn. The interrupted message stays so the user keeps its context.
   */
  const retryAfterInterruption = useCallback(async (interruptedMessageId: string, prompt?: string) => {
    const promptText = prompt ?? precedingUserPrompt(messages, interruptedMessageId);
    if (!promptText?.trim()) {
      console.warn('[useChat] retryAfterInterruption: no preceding user message found for', interruptedMessageId);
      return;
    }
    // Replay the original send payload (sans files — already attached to the
    // persisted user message) so users with non-default runners or approval
    // modes do not silently drop back to cloud / require_approval.
    const opts = lastSendOptionsRef.current;
    await sendMessage(
      promptText,
      undefined,
      opts.mode,
      opts.runnerType,
      opts.approvalMode,
      { withoutSnapshot: true },
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // ── Message feedback ───────────────────────────────────────────────────
  const submitFeedback = useCallback(async (messageId: string, rating: 'up' | 'down', comment?: string, fullContent?: string) => {
    const convId = conversationIdRef.current;
    if (!convId) return;
    await client.post(`/api/v1/conversations/${convId}/messages/${messageId}/feedback`, {
      rating,
      comment: comment || null,
      fullContent: fullContent || null,
    });
    const key = conversationQueryKey(convId);
    queryClient.setQueryData<InfiniteData<MessagePage>>(key, applyFeedbackRating(messageId, rating));
  }, [queryClient]);

  const deleteFeedback = useCallback(async (messageId: string) => {
    const convId = conversationIdRef.current;
    if (!convId) return;
    await client.delete(`/api/v1/conversations/${convId}/messages/${messageId}/feedback`);
    const key = conversationQueryKey(convId);
    queryClient.setQueryData<InfiniteData<MessagePage>>(key, clearFeedbackRating(messageId));
  }, [queryClient]);

  // ── Tool approve/reject ────────────────────────────────────────────────
  const approveToolExecutionFn = useCallback(async (toolUseId: string) => {
    const convId = conversationIdRef.current;
    if (!convId) return;
    await approveToolExecution(convId, toolUseId);
  }, []);

  const rejectToolExecutionFn = useCallback(async (toolUseId: string, comment?: string) => {
    const convId = conversationIdRef.current;
    if (!convId) return;
    await rejectToolExecution(convId, toolUseId, comment);
  }, []);

  // ── Model-swap suggestion accept/dismiss (AIB-7877) ────────────────────
  const acceptModelSuggestion = useCallback(async (runnerType?: string, approvalMode?: string) => {
    const suggestion = pendingModelSuggestion;
    if (!suggestion) return;
    setPendingModelSuggestion(null);
    setStoredLlmModel(conversationIdRef.current, suggestion.suggestedModel);
    await sendMessage(
      MODEL_SWAP_PROCEED_MESSAGE, undefined, undefined, runnerType, approvalMode,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingModelSuggestion]);

  const dismissModelSuggestion = useCallback(async (runnerType?: string, approvalMode?: string) => {
    const suggestion = pendingModelSuggestion;
    if (!suggestion) return;
    setPendingModelSuggestion(null);
    addDismissedModelSuggestion(conversationIdRef.current, suggestion.taskType);
    await sendMessage(
      MODEL_KEEP_PROCEED_MESSAGE, undefined, undefined, runnerType, approvalMode,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingModelSuggestion]);

  // ── Clarification questions submit/skip (AIB-6745) ─────────────────────
  const submitAskUser = useCallback(async (
    answers: AskUserAnswer[], crops?: File[], runnerType?: string, approvalMode?: string,
  ) => {
    if (answers.length === 0) return;
    const previous = pendingAskUser;
    const epoch = conversationEpochRef.current;
    setPendingAskUser(null);
    // Region crops ride as attachments on this same turn so the agent sees the
    // exact area the user marked; the folded answer text points at each by name.
    const sent = await sendMessage('', crops, undefined, runnerType, approvalMode,
      { clarification: { questionAnswers: answers } });
    // Nothing was dispatched (crop upload or pre-dispatch failure): restore the
    // card seeded with the answers and drawn region so the user can retry
    // without redrawing. (A mid-stream failure returns true — turn in flight.)
    if (!sent && previous && epoch === conversationEpochRef.current) {
      setPendingAskUser({ ...previous, restore: { answers, crops: crops ?? [] } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAskUser]);

  /** User answered in free chat instead of the card: forward their text plus the option legend. */
  const skipAskUser = useCallback(async (message: string, files?: File[], runnerType?: string, approvalMode?: string) => {
    const pending = pendingAskUser;
    setPendingAskUser(null);
    const legend: ClarificationItem[] | undefined = pending
      ? pending.questions.map((q) => ({ question: q.question, options: q.answers }))
      : undefined;
    await sendMessage(message, files, undefined, runnerType, approvalMode,
      { clarification: { questionsSkipped: true, questionAnswers: legend } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAskUser]);

  const clearConversation = useCallback(() => {
    conversationIdRef.current = null;
    currentAssistantTextRef.current = '';
    assistantMessageIdRef.current = null;
    setResolvedConversationId(null);
    setPendingModelSuggestion(null);
    setPendingAskUser(null);
    resetSetup();
  }, [resetSetup]);

  const getErrorMessage = (): string | null => {
    if (messagesError instanceof Error) return messagesError.message;
    if (sendError) return sendError;
    return null;
  };

  return {
    messages,
    isLoadingMessages,
    isHistorySettled,
    isSending,
    isReconnecting,
    error: getErrorMessage(),
    conversationId: conversationId || null,
    resolvedConversationId,
    sendMessage,
    cancelRequest,
    clearConversation,
    pendingModelSuggestion,
    clearPendingModelSuggestion: useCallback(() => setPendingModelSuggestion(null), []),
    acceptModelSuggestion,
    dismissModelSuggestion,
    pendingAskUser,
    clearPendingAskUser: useCallback(() => setPendingAskUser(null), []),
    submitAskUser,
    skipAskUser,
    approveToolExecution: approveToolExecutionFn,
    rejectToolExecution: rejectToolExecutionFn,
    submitFeedback,
    deleteFeedback,
    retryAfterInterruption,
    setupSteps,
    setupExiting,
    compacting,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
};
